import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Button, Stepper, Step, StepLabel,
  Select, MenuItem, FormControl, InputLabel, Breadcrumbs, Link, LinearProgress, styled, IconButton, Snackbar, Alert, CircularProgress, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, TextField,
  List, ListItem, ListItemButton, ListItemText,
  Autocomplete,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SyncIcon from '@mui/icons-material/Sync';
import MappingIcon from '@mui/icons-material/AccountTree';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import * as XLSX from 'xlsx';
import { useUserRights } from '../../context/UserRightsContext';
import { AuditLoggers } from '../../utils/auditLogger';
import { splitSurrogateKeyTokens } from '../../utils/surrogateKeyTokens';
import { useToast } from '../hooks/useToast';
import Toast from '../common/Toast';
import RefTemplateMappingsEditorDrawer from './GLSync/RefTemplateMappingsEditorDrawer';
import {
  buildAdditionalAttributesFromCombinationRow,
  buildAdditionalAttributesFromHeaderIndexRow,
  buildAdditionalAttributesFromTemplateRow,
  buildGlAccountRowForS3NativeKeys,
  compactGlAccountRowForUpload,
  getTemplateMappedCanonicalUpperSet,
  getTemplateMappedExcelHeadersLower,
} from './glChartOfAccountUploadExtras';
import { stripGlSystemLabel, toChartOfAccountGlSystem } from '../../constants/glSyncDocumentTypes';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateProceedButtonSx,
  harmonateUploadOutlinedSx,
  harmonateCancelButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateDialogContentSx,
  harmonateFormFieldsSx,
  harmonateGradientFormFieldsSx,
} from '../../theme/harmonateStyles';

const UploadArea = styled(Box)(({ theme }) => ({
  border: `2px dashed ${ht.line}`,
  borderRadius: '8px',
  padding: '30px 20px',
  textAlign: 'center',
  backgroundColor: ht.listBg,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: ht.blue,
    backgroundColor: ht.secondaryTab,
  },
}));

const HiddenInput = styled('input')({
  display: 'none',
});

/** First-row column headers with stable 0-based index (for Source → Combination UI). */
const extractSpreadsheetColumnHeaders = (file, arrayBuffer) => {
  if (file.name.endsWith('.csv')) {
    const text = new TextDecoder().decode(arrayBuffer);
    const rows = text.split('\n').map((row) => {
      const regex = /,(?=(?:(?:[^"]*"){2})*[^"]*$)/;
      return row.split(regex).map((cell) => cell.replace(/^"|"$/g, '').trim());
    });
    const headers = rows[0] || [];
    return headers.map((name, index) => ({
      index,
      name: name == null ? '' : String(name),
    }));
  }
  if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
    const row0 = json[0] || [];
    return row0.map((cell, index) => ({
      index,
      name: cell == null ? '' : String(cell).trim(),
    }));
  }
  throw new Error('Unsupported file type. Please upload a .csv, .xls, or .xlsx file.');
};

const normalizeSurrogateHeaderToken = (s) =>
  String(s || '')
    .trim()
    .replace(/\s+/g, '_')
    .toUpperCase();

/** Min 1, max 5: column 0 → glCode1; columns 1…n−1 → non-empty values joined with "." → glCode2 (may be empty). */
const GL_UPLOAD_COMBINATION_MIN = 1;
const GL_UPLOAD_COMBINATION_MAX = 5;

/** Map tenant-system `surrogateKey` (pipe-separated header tokens) to combination columns (1–GL_UPLOAD_COMBINATION_MAX). */
const resolveUploadCombinationFromSurrogateKey = (surrogateKey, headers) => {
  if (!surrogateKey || !Array.isArray(headers) || headers.length === 0) return null;
  const parts = splitSurrogateKeyTokens(surrogateKey);
  if (parts.length < 1 || parts.length > GL_UPLOAD_COMBINATION_MAX) return null;

  const findCol = (token) => {
    const t = normalizeSurrogateHeaderToken(token);
    return headers.find((h) => normalizeSurrogateHeaderToken(h.name) === t);
  };

  const cols = [];
  for (const part of parts) {
    const c = findCol(part);
    if (!c) return null;
    cols.push(c);
  }
  const indices = cols.map((c) => c.index);
  if (new Set(indices).size !== indices.length) return null;
  return cols;
};

const findUploadSystemRow = (systems, selectedName) => {
  if (!selectedName?.trim() || !Array.isArray(systems)) return undefined;
  const needle = String(selectedName).trim();
  return (
    systems.find((s) => String(s.name ?? '').trim() === needle) ||
    systems.find((s) => String(s.name ?? '').trim().toLowerCase() === needle.toLowerCase())
  );
};

/** Ref-template mapping normalization (parity with GL Accounts upload flow). */
const normalizeUploadRefMappingFromApi = (mappingObj) => {
  const out = {};
  if (!mappingObj || typeof mappingObj !== 'object') return out;
  Object.entries(mappingObj).forEach(([rawKey, v]) => {
    const key = String(rawKey || '').trim();
    if (!key || !v || v.isActive === false) return;
    out[key] = {
      ...v,
      targetColumn: String(v.targetColumn != null ? v.targetColumn : '').trim() || key,
      dataType: v.dataType || 'TEXT',
      isActive: v.isActive !== false,
    };
  });
  return out;
};

const initializeUploadTemplateExcelColumnMap = (template, excelFieldNames) => {
  const next = {};
  Object.entries(template?.mappings || {}).forEach(([templateKeyRaw, m]) => {
    const templateKey = String(templateKeyRaw || '').trim();
    if (!m || m.isActive === false) return;
    const expectedHeader = String(m.targetColumn || templateKey).trim();
    const auto = excelFieldNames.find(
      (f) => String(f).trim().toLowerCase() === expectedHeader.toLowerCase()
    );
    next[templateKey] = auto ? String(auto) : '';
  });
  return next;
};

const GL_ACCOUNTS_TEMPLATE_CANONICAL = new Set([
  'GL_CODE', 'GL_DESCRIPTION', 'GL_ACCOUNT', 'TRANSACTION_TYPE', 'LOT_NO', 'BATCH_ID', 'CCY_CODE', 'COMMENTS',
  'CURRENCY', 'QUANTITY', 'SCY_CODE', 'FUND_CODE', 'FUND_NAME', 'ACCOUNT_NO', 'AMOUNT_CCY', 'AMOUNT_SCY',
  'DESCRIPTION', 'ISSUER_CODE', 'SECURITY_NO', 'ACCOUNT_TYPE', 'COMPANY_CODE', 'COMPANY_TYPE', 'DEBIT_AMOUNT',
  'POSTING_DATE', 'TOTAL_AMOUNT', 'CREDIT_AMOUNT', 'DOCUMENT_DATE', 'GL_JOIN_FIELD', 'INVESTOR_CODE',
  'INVESTOR_NAME', 'LOCAL_CURRENCY', 'SOURCE_FILE_GL', 'TRANSACTION_DATE', 'INVESTOR_POOL_CODE',
  'LOCAL_DEBIT_AMOUNT', 'LOCAL_TOTAL_AMOUNT', 'LOCAL_CREDIT_AMOUNT', 'EXTERNAL_DOCUMENT_NO',
]);

const GL_ACCOUNT_REF_TARGET_TO_CANONICAL = {
  transactionDate: 'TRANSACTION_DATE',
  glAccountName: 'GL_DESCRIPTION',
  glAccountNo: 'GL_CODE',
  glCode: 'GL_CODE',
  glDescription: 'GL_DESCRIPTION',
  glAccount: 'GL_ACCOUNT',
  accountDisplay: 'GL_ACCOUNT',
  transactionType: 'TRANSACTION_TYPE',
};

const convertGlAccountTemplateCellValue = (value, dataType) => {
  if (value === '' || value == null) return '';
  const type = (dataType || 'TEXT').toUpperCase();
  if (['NUMBER', 'INTEGER', 'FLOAT', 'DECIMAL'].includes(type)) {
    const strValue = String(value).trim();
    const numValue = strValue.replace(/[^0-9.-]/g, '');
    if (numValue === '' || numValue === '-') return '';
    return Number(numValue);
  }
  return String(value).replace(/\s+/g, ' ').trim();
};

/** After template mapping, drop raw file headers so uploaded JSON is canonical-only (parity with GLSyncGLMappingWorkflow). */
const stripMappedSourceColumnsFromRow = (out, sourceRow, excelField, preserveKeysLower = new Set()) => {
  const base = String(excelField || '').trim().toLowerCase();
  if (!base) return;
  for (const k of [...Object.keys(out)]) {
    if (preserveKeysLower.has(k.toLowerCase())) continue;
    if (!Object.prototype.hasOwnProperty.call(sourceRow, k)) continue;
    const kl = k.toLowerCase();
    if (kl === base || kl.startsWith(`${base}_`)) {
      delete out[k];
    }
  }
};

const applyGlAccountRefTemplateToRows = (excelData, template, excelColumnByTemplateField) => {
  const mappings = template?.mappings || {};
  const fieldSpecs = [];
  Object.entries(mappings).forEach(([templateKeyRaw, m]) => {
    if (!m || m.isActive === false) return;
    const templateKey = String(templateKeyRaw).trim();
    const excelField = excelColumnByTemplateField?.[templateKey] ?? excelColumnByTemplateField?.[templateKeyRaw];
    if (!excelField || String(excelField).trim() === '') return;
    const targetCol = String(m.targetColumn || '').trim();
    const tkUpper = templateKey.toUpperCase();
    let canonical = GL_ACCOUNT_REF_TARGET_TO_CANONICAL[targetCol] || GL_ACCOUNT_REF_TARGET_TO_CANONICAL[templateKey] || null;
    if (!canonical && /^[A-Z0-9_]+$/.test(tkUpper)) canonical = tkUpper;
    if (!canonical && targetCol && targetCol === targetCol.toUpperCase() && /^[A-Z0-9_]+$/.test(targetCol)) canonical = targetCol;
    if (!canonical || !GL_ACCOUNTS_TEMPLATE_CANONICAL.has(canonical)) return;
    fieldSpecs.push({
      excelField: String(excelField).trim(),
      canonical,
      dataType: m.dataType || 'TEXT',
    });
  });
  if (fieldSpecs.length === 0) return excelData;
  return excelData.map((row) => {
    const out = { ...row };
    fieldSpecs.forEach(({ excelField, canonical, dataType }) => {
      if (!(excelField in row)) return;
      const v = convertGlAccountTemplateCellValue(row[excelField], dataType);
      if (v !== '' && v != null) out[canonical] = v;
      stripMappedSourceColumnsFromRow(out, row, excelField, new Set([String(canonical).toLowerCase()]));
    });
    return out;
  });
};

const resolveGlAccountTemplateFieldCanonical = (templateKeyRaw, spec) => {
  if (!spec || spec.isActive === false) return null;
  const templateKey = String(templateKeyRaw).trim();
  const targetCol = String(spec.targetColumn || '').trim();
  const tkUpper = templateKey.toUpperCase();
  let canonical =
    GL_ACCOUNT_REF_TARGET_TO_CANONICAL[targetCol] ||
    GL_ACCOUNT_REF_TARGET_TO_CANONICAL[templateKey] ||
    null;
  if (!canonical && /^[A-Z0-9_]+$/.test(tkUpper)) canonical = tkUpper;
  if (!canonical && targetCol && targetCol === targetCol.toUpperCase() && /^[A-Z0-9_]+$/.test(targetCol)) {
    canonical = targetCol;
  }
  if (!canonical || !GL_ACCOUNTS_TEMPLATE_CANONICAL.has(canonical)) return null;
  return canonical;
};

const GL_ACCOUNT_UPLOAD_REQUIRED_HEADERS = ['GL_CODE', 'GL_ACCOUNT', 'GL_DESCRIPTION'];

const extractGlAccountFieldsFromSheet = async (file) => {
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];
  const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
  if (!jsonData || jsonData.length === 0) return { fields: [], data: [] };
  const headers = jsonData[0].map((h) => (h ? h.toString().trim() : '')).filter((h) => h !== '');
  const rows = jsonData.slice(1).filter((row) => row.some((cell) => cell !== '' && cell !== null));
  const dataRows = rows.map((row) => {
    const obj = {};
    headers.forEach((header, index) => { obj[header] = row[index] ?? ''; });
    return obj;
  });
  return { fields: headers, data: dataRows };
};

const glAccountMappedObjectsToParsedRows = (mappedRows, glSystem) =>
  mappedRows.map((row) => {
    const code = String(row.GL_CODE ?? '').trim();
    const tt = String(row.TRANSACTION_TYPE ?? '').trim();
    const glDescription = String(row.GL_DESCRIPTION ?? '').trim();
    const glAccount = String(row.GL_ACCOUNT ?? '').trim();
    return {
      glCode1: code,
      glCode2: tt,
      glDescription,
      glAccount,
      accountType: String(row.ACCOUNT_TYPE ?? '').trim() || null,
      accountSubType: String(row.ACCOUNT_SUB_TYPE ?? '').trim() || null,
      normalBalance: String(row.NORMAL_BALANCE ?? '').trim() || null,
      groupCode: String(row.GROUP_CODE ?? '').trim() || null,
      currency: String(row.CURRENCY ?? '').trim() || null,
      transactionType: tt || null,
      glJoinField: String(row.GL_JOIN_FIELD ?? '').trim() || null,
      glSystem: String(glSystem || '').toUpperCase(),
      glSystemVersion: '1.0',
    };
  });

/** Source grid label from chart-of-account row: glCode - glDescription (fallbacks if partial). */
const formatGlCodeDescriptionLabel = (row) => {
  const code = String(row?.glCode ?? '').trim();
  const desc = String(row?.glDescription ?? '').trim();
  if (code && desc) return `${code} - ${desc}`;
  if (code) return code;
  if (desc) return desc;
  const acct = String(row?.glAccount ?? '').trim();
  return acct || '—';
};

/** Match dropdown value (formatted label or legacy glAccount) to a chart-of-account row. */
const findDestinationRowBySelectValue = (accounts, value) => {
  if (!value || value === '-- No Mapping --' || !Array.isArray(accounts)) return undefined;
  const v = String(value).trim();
  return accounts.find(
    (d) => formatGlCodeDescriptionLabel(d) === v || String(d.glAccount ?? '').trim() === v
  );
};

const stripGlSystemLabelForNav = (value) => stripGlSystemLabel(value);

const fetchDefaultCashReconPropertyId = async (tenantId, token) => {
  if (!tenantId || !token) return null;
  try {
    const baseUrl = (process.env.REACT_APP_BACKEND_URL).replace(/\/+$/, '');
    const url = new URL(`${baseUrl}/conductor/cash-recon/properties/by-tenant-and-status`);
    url.searchParams.set('tenant_id', tenantId);
    url.searchParams.set('status', 'ACTIVE');
    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });
    if (!res.ok) {
      console.warn('cash-recon properties by-tenant-and-status failed:', res.status);
      return null;
    }
    const list = await res.json().catch(() => []);
    if (!Array.isArray(list) || list.length === 0) return null;
    const byName = list.find((p) => String(p.propertyName || '').toLowerCase().trim() === 'default');
    const byCode = list.find((p) => String(p.propertyCode || '').toUpperCase().trim() === 'DF');
    const chosen = byName || byCode || list[0];
    return chosen?.propertyId ?? null;
  } catch (e) {
    console.error('fetchDefaultCashReconPropertyId:', e);
    return null;
  }
};

/** Popper flips the list above the field when space below looks tight (common in dialogs). Prefer opening downward. */
const glSyncSystemAutocompleteComponentsProps = {
  popper: {
    placement: 'bottom-start',
    modifiers: [{ name: 'flip', enabled: false }],
    sx: { zIndex: 1800 },
  },
};

const GLSyncMapping = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sourceGL = '', destinationGL = '' } = location.state || {};

  const steps = ['GL Mapping Check', 'Trial Balance Comparison'];
  const { hasPermission, isHarmonateUser } = useUserRights();
  const { toastState, showToast, hideToast } = useToast();

  const [mappingData, setMappingData] = useState([]); // Source data
  const [destinationData, setDestinationData] = useState([]); // Full destination data
  const [destinationOptions, setDestinationOptions] = useState(['-- No Mapping --']);
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedDestination, setSelectedDestination] = useState('');
  /** Optional fund scope (public.funds). Null = unscoped mapping. */
  const [funds, setFunds] = useState([]);
  const [loadingFunds, setLoadingFunds] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);
  /** Label stored on the mapping header (`mapping-headers` API); optional — falls back to "SOURCE to TARGET Mapping" when empty on save. */
  const [mappingDescription, setMappingDescription] = useState('');
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isSavingMapping, setIsSavingMapping] = useState(false);
  const [systems, setSystems] = useState([]);
  const [loadingSystems, setLoadingSystems] = useState(false);
  const [loadingGlAccounts, setLoadingGlAccounts] = useState(false);

  // Upload popup states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [refreshingMapping, setRefreshingMapping] = useState(false);
  const [pushingMapping, setPushingMapping] = useState(false);
  /** null | 'refresh' | 'push' */
  const [mappingConfirmAction, setMappingConfirmAction] = useState(null);
  const [selectedGlSystem, setSelectedGlSystem] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  // Add these state variables
  const [unitOfWorkId, setUnitOfWorkId] = useState('');
  const [currentStep, setCurrentStep] = useState('');
  const [uploadError, setUploadError] = useState(null);
  const [uploadHeaderNotes, setUploadHeaderNotes] = useState('');
  const [uploadFileHeaders, setUploadFileHeaders] = useState([]);
  const [uploadColumnCombination, setUploadColumnCombination] = useState([]);
  const [uploadSelectedSourceColumn, setUploadSelectedSourceColumn] = useState(null);
  const [uploadHeadersLoading, setUploadHeadersLoading] = useState(false);
  const API_BASE = process.env.REACT_APP_API_URL || '';

  const [documentTypes, setDocumentTypes] = useState([]);
  const [loadingDocumentTypes, setLoadingDocumentTypes] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [uploadTemplates, setUploadTemplates] = useState([]);
  const [loadingUploadTemplates, setLoadingUploadTemplates] = useState(false);
  const [selectedUploadTemplate, setSelectedUploadTemplate] = useState('');
  const [uploadExcelColumnByTemplateField, setUploadExcelColumnByTemplateField] = useState({});
  const [showMissingTemplateFields, setShowMissingTemplateFields] = useState(false);
  const [uploadTemplateMappingsEditorOpen, setUploadTemplateMappingsEditorOpen] = useState(false);
  /** Raw API `mapping` overlays from the template drawer for this upload dialog session only (no PUT). */
  const [localUploadRefTemplateMappingById, setLocalUploadRefTemplateMappingById] = useState({});

  // Add these state variables near the top with other state declarations
  const [fuzzyMatches, setFuzzyMatches] = useState({});
  const [loadingFuzzyMatches, setLoadingFuzzyMatches] = useState(false);
  const [aiMatching, setAiMatching] = useState(false);
  const [aiMatchTypes, setAiMatchTypes] = useState({});

  const [isDragging, setIsDragging] = useState(false);

  const uploadCombinationPathActive = useMemo(
    () => uploadColumnCombination.length >= GL_UPLOAD_COMBINATION_MIN,
    [uploadColumnCombination.length]
  );

  const resetUploadTemplateUi = () => {
    setUploadTemplates([]);
    setSelectedUploadTemplate('');
    setUploadExcelColumnByTemplateField({});
    setUploadTemplateMappingsEditorOpen(false);
  };

  const resolveSnowflakeTenantId = () => {
    const raw = String(localStorage.getItem('tenant_integration') || '').trim();
    if (!raw) return '';
    const upper = raw.toUpperCase();
    const dbToLogical = { DEV_T_101: 'T_247' };
    if (dbToLogical[upper]) return dbToLogical[upper];
    if (upper.startsWith('T_')) return upper;
    if (/^\d+$/.test(upper)) return `T_${upper}`;
    return upper;
  };

  const handleRefreshMapping = async () => {
    if (refreshingMapping || pushingMapping) return;
    const token = localStorage.getItem('token');
    const tenantUuid = localStorage.getItem('tenant_id');
    const snowflakeTenantId = resolveSnowflakeTenantId();
    if (!token) {
      showToast({ message: 'Authentication required', severity: 'error' });
      return;
    }
    if (!tenantUuid || !snowflakeTenantId) {
      showToast({
        message: 'Tenant id / integration id missing — cannot refresh mappings',
        severity: 'error',
      });
      return;
    }
    try {
      setRefreshingMapping(true);
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '/');
      const url = new URL(`${baseUrl}fastapi/v1/gl-sync/gl-account-mappings/sync-from-snowflake`);
      url.searchParams.set('snowflake_tenant_id', snowflakeTenantId);
      url.searchParams.set('tenant_uuid', tenantUuid);
      const resp = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = body?.detail || body?.message || resp.statusText || 'Refresh Mapping failed';
        throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      }
      const inserted = Number(body?.inserted) || 0;
      const updated = Number(body?.updated) || 0;
      const skipped = Number(body?.skipped) || 0;
      const fetched = Number(body?.fetched_rows) || 0;
      const headersCreated = Number(body?.mapping_headers_created) || 0;
      showToast({
        message:
          body?.message ||
          `Mappings refreshed from Snowflake (${body?.snowflake_tenant_id || snowflakeTenantId}). Fetched ${fetched}, inserted ${inserted}, updated ${updated}, skipped ${skipped}. Mapping cards created: ${headersCreated}.`,
        severity: 'success',
      });
      setMappingConfirmAction(null);
    } catch (error) {
      showToast({
        message: error?.message || 'Failed to refresh mappings from Snowflake',
        severity: 'error',
      });
    } finally {
      setRefreshingMapping(false);
    }
  };

  const handlePushMapping = async () => {
    if (refreshingMapping || pushingMapping) return;
    const token = localStorage.getItem('token');
    const tenantUuid = localStorage.getItem('tenant_id');
    const snowflakeTenantId = resolveSnowflakeTenantId();
    if (!token) {
      showToast({ message: 'Authentication required', severity: 'error' });
      return;
    }
    if (!tenantUuid || !snowflakeTenantId) {
      showToast({
        message: 'Tenant id / integration id missing — cannot push mappings',
        severity: 'error',
      });
      return;
    }
    try {
      setPushingMapping(true);
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '/');
      const url = new URL(`${baseUrl}fastapi/v1/gl-sync/gl-account-mappings/sync-to-snowflake`);
      url.searchParams.set('snowflake_tenant_id', snowflakeTenantId);
      url.searchParams.set('tenant_uuid', tenantUuid);
      if (selectedSource) {
        const fromSrc = toChartOfAccountGlSystem(selectedSource);
        if (fromSrc) url.searchParams.set('from_gl_source', fromSrc);
      }
      if (selectedDestination) {
        const toSrc = toChartOfAccountGlSystem(selectedDestination);
        if (toSrc) url.searchParams.set('to_gl_source', toSrc);
      }
      if (selectedFund?.fundCode) {
        url.searchParams.set('fund_filter', 'true');
        url.searchParams.set('fund_code', selectedFund.fundCode);
      }
      const resp = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const detail = body?.detail || body?.message || resp.statusText || 'Push Mapping failed';
        throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      }
      const upserted = Number(body?.upserted) || 0;
      const skipped = Number(body?.skipped) || 0;
      const fetched = Number(body?.fetched_rows) || 0;
      showToast({
        message:
          body?.message ||
          `Mappings pushed to Snowflake (${body?.snowflake_tenant_id || snowflakeTenantId}). Fetched ${fetched}, upserted ${upserted}, skipped ${skipped}.`,
        severity: 'success',
      });
      setMappingConfirmAction(null);
    } catch (error) {
      showToast({
        message: error?.message || 'Failed to push mappings to Snowflake',
        severity: 'error',
      });
    } finally {
      setPushingMapping(false);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ['.csv', '.xls', '.xlsx'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

      if (validTypes.includes(fileExtension)) {
        setUploadFile(file);
      } else {
        setUploadError('Invalid file type. Please upload a CSV or Excel file.');
      }
    }
  };

  const uploadSourceColumnsAvailable = useMemo(() => {
    const used = new Set(uploadColumnCombination.map((c) => c.index));
    return uploadFileHeaders.filter((h) => !used.has(h.index));
  }, [uploadFileHeaders, uploadColumnCombination]);

  useEffect(() => {
    if (!uploadFile) {
      setUploadFileHeaders([]);
      setUploadColumnCombination([]);
      setUploadSelectedSourceColumn(null);
      setUploadHeadersLoading(false);
      return undefined;
    }

    let cancelled = false;
    setUploadHeadersLoading(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        if (cancelled) return;
        const headers = extractSpreadsheetColumnHeaders(uploadFile, e.target.result);
        setUploadFileHeaders(headers);
        setUploadColumnCombination([]);
        setUploadSelectedSourceColumn(null);
      } catch (err) {
        if (!cancelled) {
          showToast({
            message: err.message || 'Could not read file headers',
            severity: 'error',
            autoHideDuration: 8000,
          });
          setUploadFile(null);
        }
      } finally {
        if (!cancelled) {
          setUploadHeadersLoading(false);
        }
      }
    };
    reader.onerror = () => {
      if (!cancelled) {
        setUploadHeadersLoading(false);
        showToast({
          message: 'Failed to read file',
          severity: 'error',
          autoHideDuration: 8000,
        });
        setUploadFile(null);
      }
    };
    reader.readAsArrayBuffer(uploadFile);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- showToast stable for this effect
  }, [uploadFile]);

  useEffect(() => {
    let cancelled = false;

    const fetchUploadTemplates = async () => {
      if (!uploadDialogOpen || !selectedDocumentType || !selectedGlSystem) {
        setUploadTemplates([]);
        setSelectedUploadTemplate('');
        return;
      }
      const uploadSystem = findUploadSystemRow(systems, selectedGlSystem);
      const glSystemId = String(uploadSystem?.systemId || uploadSystem?.system_id || uploadSystem?.id || '').trim();
      if (!glSystemId) {
        setUploadTemplates([]);
        setSelectedUploadTemplate('');
        return;
      }
      const token = getAuthToken();
      if (!token) {
        setUploadTemplates([]);
        setSelectedUploadTemplate('');
        return;
      }

      setLoadingUploadTemplates(true);
      try {
        const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
        const url = `${baseUrl}/conductor/ref-templates/by-document-and-gl?documentTypeId=${encodeURIComponent(
          selectedDocumentType
        )}&glSystemId=${encodeURIComponent(glSystemId)}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          if (!cancelled) {
            setUploadTemplates([]);
            setSelectedUploadTemplate('');
          }
          return;
        }
        const data = await response.json();
        const transformed = Array.isArray(data)
          ? data.map((item) => {
            let rawMapping = item.mapping;
            if (typeof rawMapping === 'string') {
              try {
                rawMapping = JSON.parse(rawMapping);
              } catch {
                rawMapping = {};
              }
            }
            if (!rawMapping || typeof rawMapping !== 'object') rawMapping = {};
            const tid = item.templateId ?? item.id;
            return {
              id: tid,
              name: item.templateName ?? item.name ?? 'Template',
              description: item.description || '',
              mappings: normalizeUploadRefMappingFromApi(rawMapping),
              apiTemplate: {
                ...item,
                templateId: tid,
                templateName: item.templateName ?? item.name ?? 'Template',
                mapping: rawMapping,
              },
            };
          })
          : [];
        if (!cancelled) {
          setUploadTemplates(transformed);
          setSelectedUploadTemplate((prev) =>
            prev && transformed.some((x) => x.id === prev) ? prev : transformed[0]?.id || ''
          );
        }
      } catch (error) {
        console.error('Error fetching upload ref-templates:', error);
        if (!cancelled) {
          setUploadTemplates([]);
          setSelectedUploadTemplate('');
        }
      } finally {
        if (!cancelled) setLoadingUploadTemplates(false);
      }
    };

    void fetchUploadTemplates();
    return () => {
      cancelled = true;
    };
  }, [uploadDialogOpen, selectedDocumentType, selectedGlSystem, systems]);

  useEffect(() => {
    if (!uploadDialogOpen) {
      setUploadTemplateMappingsEditorOpen(false);
    }
  }, [uploadDialogOpen]);

  useEffect(() => {
    setLocalUploadRefTemplateMappingById({});
  }, [selectedGlSystem, selectedDocumentType]);

  useEffect(() => {
    if (!uploadDialogOpen) {
      setLocalUploadRefTemplateMappingById({});
    }
  }, [uploadDialogOpen]);

  const activeUploadTemplate = useMemo(() => {
    if (!selectedUploadTemplate) return null;
    const base = uploadTemplates.find((t) => t.id === selectedUploadTemplate);
    if (!base) return null;
    const localRaw = localUploadRefTemplateMappingById[selectedUploadTemplate];
    if (!localRaw) return base;
    return {
      ...base,
      mappings: normalizeUploadRefMappingFromApi(localRaw),
      apiTemplate: { ...base.apiTemplate, mapping: localRaw },
    };
  }, [uploadTemplates, selectedUploadTemplate, localUploadRefTemplateMappingById]);

  const activeUploadApiTemplateForEditor = useMemo(() => {
    if (!selectedUploadTemplate) return null;
    const base = uploadTemplates.find((t) => t.id === selectedUploadTemplate)?.apiTemplate ?? null;
    if (!base) return null;
    const localRaw = localUploadRefTemplateMappingById[selectedUploadTemplate];
    if (!localRaw) return base;
    return { ...base, mapping: localRaw };
  }, [uploadTemplates, selectedUploadTemplate, localUploadRefTemplateMappingById]);

  useEffect(() => {
    if (!selectedUploadTemplate) {
      setUploadExcelColumnByTemplateField((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }
    const t = activeUploadTemplate;
    if (!t) return;
    const names = uploadFileHeaders.map((h) => String(h.name ?? '').trim()).filter(Boolean);
    if (names.length === 0) {
      setUploadExcelColumnByTemplateField({});
      return;
    }
    setUploadExcelColumnByTemplateField(initializeUploadTemplateExcelColumnMap(t, names));
  }, [selectedUploadTemplate, activeUploadTemplate, uploadFileHeaders]);

  const uploadTemplateColumnRows = useMemo(() => {
    if (!activeUploadTemplate) return [];
    const headerNames = uploadFileHeaders.map((h) => String(h.name ?? '').trim()).filter(Boolean);
    return Object.entries(activeUploadTemplate.mappings || {})
      .map(([templateKeyRaw, spec]) => {
        const templateKey = String(templateKeyRaw).trim();
        if (!spec || spec.isActive === false) return null;
        const expectedHeader = String(spec.targetColumn || templateKey).trim();
        const selectedCol = String(uploadExcelColumnByTemplateField[templateKey] || '').trim();
        const autoMatched = headerNames.find(
          (hn) => hn.toLowerCase() === expectedHeader.toLowerCase()
        );
        const displayFileCol = selectedCol || autoMatched || 'Missing';
        const matched = Boolean(displayFileCol && displayFileCol !== 'Missing');
        const canonicalKey = resolveGlAccountTemplateFieldCanonical(templateKeyRaw, spec);
        return {
          templateKey,
          expectedHeader,
          matched,
          displayFileCol,
          canonicalKey,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.matched === b.matched) return 0;
        return a.matched ? -1 : 1;
      });
  }, [activeUploadTemplate, uploadFileHeaders, uploadExcelColumnByTemplateField]);

  const missingTemplateColumnCount = useMemo(
    () => uploadTemplateColumnRows.filter((row) => !row.matched).length,
    [uploadTemplateColumnRows]
  );

  const glAccountUploadMandatoryMessage = useMemo(() => {
    if (!uploadFile || uploadHeadersLoading || uploadFileHeaders.length === 0) return null;
    const raw = new Set(
      uploadFileHeaders.map((h) => String(h.name ?? '').trim().toUpperCase()).filter(Boolean)
    );
    if (uploadCombinationPathActive) {
      if (!raw.has('GL_ACCOUNT') || !raw.has('GL_DESCRIPTION')) {
        return 'Row 1 must include GL_ACCOUNT and GL_DESCRIPTION. GL_CODE is built from your combination columns.';
      }
      return null;
    }
    if (selectedUploadTemplate && activeUploadTemplate) {
      for (const req of GL_ACCOUNT_UPLOAD_REQUIRED_HEADERS) {
        const ok = uploadTemplateColumnRows.some((row) => row.canonicalKey === req && row.matched);
        if (!ok) {
          return `Map and match all of: ${GL_ACCOUNT_UPLOAD_REQUIRED_HEADERS.join(', ')}. Missing or unmatched: ${req}.`;
        }
      }
      return null;
    }
    const missing = GL_ACCOUNT_UPLOAD_REQUIRED_HEADERS.filter((k) => !raw.has(k));
    if (missing.length > 0) {
      return `Row 1 must include: ${missing.join(', ')}`;
    }
    return null;
  }, [
    uploadFile,
    uploadHeadersLoading,
    uploadFileHeaders,
    uploadCombinationPathActive,
    selectedUploadTemplate,
    activeUploadTemplate,
    uploadTemplateColumnRows,
  ]);

  const visibleUploadTemplateColumnRows = useMemo(() => {
    if (showMissingTemplateFields) return uploadTemplateColumnRows.filter((row) => !row.matched);
    return uploadTemplateColumnRows.filter((row) => row.matched);
  }, [uploadTemplateColumnRows, showMissingTemplateFields]);

  useEffect(() => {
    if (systems.length === 0) {
      return;
    }
    if (uploadFileHeaders.length === 0) {
      setUploadColumnCombination([]);
      setUploadSelectedSourceColumn(null);
      return;
    }
    if (!selectedGlSystem?.trim()) {
      setUploadColumnCombination([]);
      setUploadSelectedSourceColumn(null);
      return;
    }
    const system = findUploadSystemRow(systems, selectedGlSystem);
    const sk = system?.surrogateKey ?? system?.surrogate_key;
    if (!sk || String(sk).trim() === '') {
      setUploadColumnCombination([]);
      setUploadSelectedSourceColumn(null);
      return;
    }
    const combo = resolveUploadCombinationFromSurrogateKey(sk, uploadFileHeaders);
    if (!combo) {
      setUploadColumnCombination([]);
      setUploadSelectedSourceColumn(null);
      return;
    }
    setUploadColumnCombination((prev) => {
      const same =
        prev.length === combo.length &&
        prev.every((p, i) => p.index === combo[i].index);
      if (same) return prev;
      return combo;
    });
    setUploadSelectedSourceColumn(null);
  }, [selectedGlSystem, uploadFileHeaders, systems]);

  const suggestionLabel = (code, description) => {
    const glCode = String(code || '').trim();
    const glDescription = String(description || '').trim();
    if (glCode && glDescription) return `${glCode} - ${glDescription}`;
    return glCode;
  };

  const applyAiMatchRows = (matches) => {
    const labels = {};
    const types = {};
    (Array.isArray(matches) ? matches : []).forEach((item) => {
      const sourceCode = String(item?.source_code || '').trim();
      const targetCode = String(item?.target_code || '').trim();
      if (!sourceCode || !targetCode) return;
      labels[sourceCode] = suggestionLabel(targetCode, item.target_description);
      if (item.match_type) types[sourceCode] = item.match_type;
    });
    return { labels, types };
  };

  const fetchStoredAiMatches = async (fromGlSource, toGlSource) => {
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenant_id');
    if (!token || !tenantId) return { labels: {}, types: {} };
    try {
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
      const url = new URL(`${baseUrl}/fastapi/v1/gl-sync/ai-matching`);
      url.searchParams.set('tenant_id', tenantId);
      url.searchParams.set('from_gl_source', fromGlSource);
      url.searchParams.set('to_gl_source', toGlSource);
      if (selectedFund?.id) url.searchParams.set('fund_id', selectedFund.id);
      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      if (!response.ok) return { labels: {}, types: {} };
      const body = await response.json();
      return applyAiMatchRows(body.matches);
    } catch (err) {
      console.error('Stored AI matches failed:', err);
      return { labels: {}, types: {} };
    }
  };

  const handleAiMatching = async () => {
    if (!selectedSource || !selectedDestination || mappingData.length === 0 || destinationData.length === 0) {
      setError('Select a source GL, a destination GL, and wait for both account lists to load.');
      return;
    }
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenant_id');
    if (!token || !tenantId) {
      setError('No authentication token found');
      return;
    }
    setAiMatching(true);
    setError(null);
    try {
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
      const response = await fetch(`${baseUrl}/fastapi/v1/gl-sync/ai-matching`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          tenant_id: tenantId,
          source_gl: selectedSource,
          target_gl: selectedDestination,
          fund_id: selectedFund?.id || null,
          fund_code: selectedFund?.fundCode || null,
          source_accounts: mappingData
            .filter((row) => String(row.glCode || '').trim())
            .map((row) => ({ code: row.glCode, description: row.glDescription || '' })),
          target_accounts: destinationData
            .filter((row) => String(row.glCode || '').trim())
            .map((row) => ({ code: row.glCode, description: row.glDescription || '' })),
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `AI matching failed (${response.status})`);
      }
      const body = await response.json();
      const applied = applyAiMatchRows(body.matches);
      setFuzzyMatches((prev) => ({ ...prev, ...applied.labels }));
      setAiMatchTypes(applied.types);
    } catch (err) {
      console.error('AI matching failed:', err);
      setError(`AI matching failed: ${err.message}`);
    } finally {
      setAiMatching(false);
    }
  };

  // Add this function to fetch fuzzy matches
  const fetchFuzzyMatches = async () => {
    if (!selectedSource || !selectedDestination) return;

    setLoadingFuzzyMatches(true);
    setFuzzyMatches({});
    setAiMatchTypes({});
    const token = localStorage.getItem('token');
    const storedTenantId = localStorage.getItem('tenant_integration') || localStorage.getItem('tenant_id');

    try {
      // Ensure proper capitalization and add _GL suffix if needed
      const fromGlSource = selectedSource.endsWith('_GL')
        ? selectedSource.toUpperCase()
        : `${selectedSource.toUpperCase()}_GL`;

      // Only process if destination is selected
      let toGlSource = '';
      if (selectedDestination) {
        toGlSource = selectedDestination.endsWith('_GL')
          ? selectedDestination.toUpperCase()
          : `${selectedDestination.toUpperCase()}_GL`;
      }

      // Only make the API call if we have both source and destination
      if (!toGlSource) {
        setLoadingFuzzyMatches(false);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/gl-link-mappings/fuzzy-matched-agg/` +
        `?from_gl_source=${encodeURIComponent(fromGlSource)}` +
        `&to_gl_source=${encodeURIComponent(toGlSource)}` +
        `&tenant=${encodeURIComponent(storedTenantId)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const matchesMap = {};
      if (response.ok) {
        const data = await response.json();
        data.forEach(item => {
          matchesMap[item.from_gl_code] = item.best_matching_gl_account;
        });
      }
      const stored = await fetchStoredAiMatches(fromGlSource, toGlSource);
      setFuzzyMatches({ ...matchesMap, ...stored.labels });
      setAiMatchTypes(stored.types);
    } catch (error) {
      console.error('Error fetching fuzzy matches:', error);
    } finally {
      setLoadingFuzzyMatches(false);
    }
  };

  // Add this useEffect to call fetchFuzzyMatches when source or destination changes
  useEffect(() => {
    if (selectedSource && selectedDestination) {
      fetchFuzzyMatches();
    }
  }, [selectedSource, selectedDestination]);

  const hasAtLeastOneRowMapping = useMemo(
    () =>
      mappingData.some(
        (item) => item.destinationField && item.destinationField !== '-- No Mapping --'
      ),
    [mappingData]
  );

  const isGlMappingSaveDisabled =
    !selectedSource ||
    !selectedDestination ||
    (!isHarmonateUser && !hasPermission('GL Sync', 'add')) ||
    !hasAtLeastOneRowMapping;

  // Add this function to handle using the suggested match
  const handleUseSuggestedMatch = (index) => {
    const glCode = mappingData[index].glCode;
    const suggestedRaw = fuzzyMatches[glCode];
    if (!suggestedRaw) return;
    const destRow = findDestinationRowBySelectValue(destinationData, suggestedRaw);
    const label = destRow ? formatGlCodeDescriptionLabel(destRow) : String(suggestedRaw).trim();

    const updatedMappingData = [...mappingData];
    updatedMappingData[index].destinationField = label;
    setMappingData(updatedMappingData);
  };

  // Get auth token helper
  const getAuthToken = () => {
    return localStorage.getItem('token') || localStorage.getItem('authToken');
  };

  // Create a new unit of work
  const createUnitOfWork = async (file) => {
    setCurrentStep('Creating upload session...');
    try {
      const token = getAuthToken();
      const tenantId = localStorage.getItem('tenant_id');
      const userId = localStorage.getItem('user_id');

      const payload = {
        name: 'GL Accounts Upload',
        documents: [{
          name: file.name,
          content_type: file.type || 'application/octet-stream',
          document_type_id: selectedDocumentType,
          source_general_ledger: selectedGlSystem ? `${selectedGlSystem.toUpperCase()}_GL` : '',
          system_id: selectedGlSystem,
          fund_id: null,
          investor_id: null,
          holding_id: null
        }],
        tenant_id: tenantId,
        created_by: userId
      };

      const baseUrl = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
      const apiUrl = `${baseUrl}/${tenantId}/units-of-work`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create upload session: ${errorText}`);
      }

      const responseData = await response.json();
      setUnitOfWorkId(responseData.id);

      // Log the successful creation of unit of work
      try {
        await AuditLoggers.glMappingFileUploaded(
          file.name,
          selectedGlSystem,
          selectedDocumentType,
          {
            unitOfWorkId: responseData.id,
            documentTypeId: selectedDocumentType,
            sourceGL: selectedGlSystem
          }
        );
      } catch (logError) {
        console.error('Error logging GL mapping file upload:', logError);
        // Don't block the operation if logging fails
      }
      return responseData;
    } catch (error) {
      console.error('Error creating unit of work:', error);
      setUploadError(`Failed to create upload session: ${error.message}`);
      throw error;
    }
  };

  // Complete the multipart upload
  const completeMultipartUpload = async (fileMeta, parts) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/multipart/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          id: fileMeta.id,
          unit_of_work_id: fileMeta.unit_of_work_id,
          location: fileMeta.location,
          upload_id: fileMeta.upload_id,
          name: fileMeta.name,
          content_type: fileMeta.content_type,
          parts: parts
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to complete upload: ${response.status} ${response.statusText} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error completing upload:', error);
      setUploadError('Failed to complete upload: ' + error.message);
      throw error;
    }
  };

  // Get presigned URLs for each part
  const getPresignedUrls = async (file, unitOfWorkId, fileMeta) => {
    setCurrentStep('Preparing upload...');
    const totalParts = Math.ceil(file.size / (5 * 1024 * 1024));

    try {
      const token = getAuthToken();
      const tenantId = localStorage.getItem('tenant_id');
      const apiUrl = `${API_BASE}/multipart/urls`;

      const payload = {
        id: fileMeta.id,
        tenant_id: tenantId,
        unit_of_work_id: unitOfWorkId,
        location: fileMeta.location,
        upload_id: fileMeta.upload_id,
        name: file.name,
        content_type: file.type || 'application/octet-stream',
        total_parts: totalParts
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get upload URLs: ${response.status} ${response.statusText} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting presigned URLs:', error);
      setUploadError('Failed to prepare upload: ' + error.message);
      throw error;
    }
  };

  // Upload a single part
  const uploadPart = async (url, partData, partNumber) => {
    try {
      const response = await fetch(url, {
        method: 'PUT',
        body: partData
      });

      if (!response.ok) {
        throw new Error(`Failed to upload part ${partNumber}`);
      }

      return response.headers.get('ETag');
    } catch (error) {
      console.error(`Error uploading part ${partNumber}:`, error);
      throw error;
    }
  };

  const fetchDocumentTypes = async () => {
    setLoadingDocumentTypes(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!tenantId) {
        setError('Tenant ID not found in local storage');
        setLoadingDocumentTypes(false);
        return;
      }

      const url = new URL(`${process.env.REACT_APP_BACKEND_URL}conductor/document-type-tenant-configs/tenant/${tenantId}?category=GL`);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        setError(`Failed to load document types: ${response.status} - ${errorText}`);
        setLoadingDocumentTypes(false);
        return;
      }

      const data = await response.json();

      if (!data || data.length === 0) {
        setError('Please configure GL document types for this tenant');
        setDocumentTypes([]);
        setLoadingDocumentTypes(false);
        return;
      }

      const GL_ACCOUNT_DOC_TYPE_NAME = 'GL Account';
      const formattedData = data
        .filter(
          (item) =>
            String(item.documentTypeName || '').trim() === GL_ACCOUNT_DOC_TYPE_NAME
        )
        .map((item) => ({
          id: item.documentTypeId,
          name: item.documentTypeName,
          requireFund: false,
          requireInvestor: false,
          isMultiInvestor: false,
          integrationId: null,
          parserType: 'DOC_PARSER',
        }));

      setDocumentTypes(formattedData);
      if (formattedData.length > 0) {
        setSelectedDocumentType(formattedData[0].id);
      } else {
        setSelectedDocumentType('');
        setError(
          'GL Account document type is not configured for this tenant. Add it under GL document type tenant settings.'
        );
      }
    } catch (error) {
      console.error('Error fetching document types:', error);
      setError('Failed to fetch document types. Please try again.');
    } finally {
      setLoadingDocumentTypes(false);
    }
  };

  useEffect(() => {
    fetchSystems();
    fetchDocumentTypes();
    fetchFunds();
  }, []);

  const fetchFunds = async () => {
    setLoadingFunds(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token || !tenantId) {
        setFunds([]);
        return;
      }
      // Same endpoint + URL shape as Master Data funds tab
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/').replace(/\/+$/, '');
      const url = `${baseUrl}/conductor/funds/by-tenant-entity?tenantId=${encodeURIComponent(tenantId)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch funds: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      const list = (Array.isArray(data) ? data : [])
        .filter((item) => item.fundName || item.fundCode)
        .map((item) => ({
          id: item.id,
          fundName: item.fundName || '',
          fundCode: item.fundCode || '',
          isMultiInvestor: item.isMultiInvestor === true || item.isMultiInvestor === 'true',
          ...item,
        }))
        .sort((a, b) => String(a.fundName || '').localeCompare(String(b.fundName || '')));
      setFunds(list);
    } catch (err) {
      console.error('Error fetching funds:', err);
      setFunds([]);
      showToast({ message: `Failed to load funds: ${err.message}`, severity: 'error' });
    } finally {
      setLoadingFunds(false);
    }
  };

  // Tenant-scoped GL systems (name, surrogateKey, id, …)
  const fetchSystems = async () => {
    setLoadingSystems(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token) {
        setError('No authentication token found');
        return;
      }
      if (!tenantId) {
        setError('Tenant ID not found. Please sign in again.');
        return;
      }

      const url = new URL(`${process.env.REACT_APP_BACKEND_URL}conductor/tenant-systems/by-tenant`);
      url.searchParams.set('tenant_id', tenantId);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tenant systems: ${response.status}`);
      }

      const data = await response.json();
      const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setSystems(list);
    } catch (error) {
      console.error('Error fetching systems:', error);
      setError(`Failed to load systems: ${error.message}`);
    } finally {
      setLoadingSystems(false);
    }
  };


  /** Maps dropdown label (e.g. "Tally") to chart_of_account.gl_system (TALLY_GL). */
  const normalizeGlSystemQueryParam = (systemName) => toChartOfAccountGlSystem(systemName);

  // Chart of accounts for mapping grid: by-tenant-with-account-type-property (system_type GL_SYNC)
  // fundFilter: when true, restrict to selected fund (or unscoped rows if fundId is null)
  const fetchGlAccountsForSystem = async (systemName, { fundId = null, fundFilter = false } = {}) => {
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token || !tenantId) {
        setError('No authentication token or tenant ID found');
        return null;
      }

      if (!systemName) {
        console.log('No system name provided');
        return null;
      }

      const primaryGlSystem = normalizeGlSystemQueryParam(systemName); // e.g. TALLY_GL
      const legacyGlSystem =
        primaryGlSystem.endsWith('_GL') && primaryGlSystem.length > 3
          ? primaryGlSystem.slice(0, -3)
          : '';

      const fetchOnce = async (glSystem) => {
        const url = new URL(
          `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/chart-of-account/by-tenant-with-account-type-property`
        );
        url.searchParams.set('tenant_id', tenantId);
        url.searchParams.set('gl_system', glSystem);
        url.searchParams.set('system_type', 'GL_SYNC');
        if (fundFilter) {
          url.searchParams.set('fund_filter', 'true');
          if (fundId) {
            url.searchParams.set('fund_id', fundId);
          }
        }
        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch chart of accounts: ${response.status}`);
        }
        const data = await response.json();
        return Array.isArray(data) ? data : [];
      };

      // Prefer canonical TALLY_GL; fall back to legacy TALLY rows from older uploads
      let rows = await fetchOnce(primaryGlSystem);
      if (rows.length === 0 && legacyGlSystem && legacyGlSystem !== primaryGlSystem) {
        rows = await fetchOnce(legacyGlSystem);
      }
      return rows;
    } catch (error) {
      console.error('Error fetching GL accounts:', error);
      setError(`Failed to load GL accounts: ${error.message}`);
      return null;
    }
  };

  const reloadSourceAccountsForFundScope = async () => {
    if (!selectedSource) return;
    setLoadingGlAccounts(true);
    try {
      const sourceData = await fetchGlAccountsForSystem(selectedSource, {
        fundFilter: true,
        fundId: selectedFund?.id || null,
      });
      if (sourceData) {
        setMappingData(
          sourceData.map((item) => ({
            source: item.glSystem,
            systemName: item.glSystem,
            glCode: item.glCode,
            glDescription: item.glDescription,
            glAccount: item.glAccount,
            destinationField: '-- No Mapping --',
            highlighted: false,
          }))
        );
      } else {
        setMappingData([]);
      }
    } catch (error) {
      console.error('Error reloading source GL accounts for fund:', error);
      setError(`Failed to load source GL accounts: ${error.message}`);
    } finally {
      setLoadingGlAccounts(false);
    }
  };

  // Reload source COA when Fund changes (fund-scoped list; destination stays unfiltered)
  useEffect(() => {
    if (!selectedSource) return;
    void reloadSourceAccountsForFundScope();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-fetch source when fund scope changes
  }, [selectedFund]);

  // const handleBulkSaveGlAccounts = async (file, glSystem) => {
  //   setUploading(true);
  //   setUploadProgress(0);
  //   setUploadError(null);
  //   setCurrentStep('Starting upload...');

  //   try {
  //     // 1. Create unit of work
  //     const uowData = await createUnitOfWork(file);

  //     // Get file metadata from the unit of work response
  //     if (!uowData.documents || !Array.isArray(uowData.documents)) {
  //       throw new Error('Invalid unit of work response: missing documents array');
  //     }

  //     const fileMeta = uowData.documents.find(doc => doc?.name === file.name);
  //     if (!fileMeta) {
  //       throw new Error(`File metadata not found in unit of work response. Looking for: ${file.name}`);
  //     }

  //     // 2. Get presigned URLs
  //     const urlsData = await getPresignedUrls(file, uowData.id, fileMeta);

  //     // 3. Upload parts
  //     const totalParts = Math.ceil(file.size / (5 * 1024 * 1024));
  //     const parts = [];

  //     for (let i = 0; i < totalParts; i++) {
  //       const start = i * (5 * 1024 * 1024);
  //       const end = Math.min(start + (5 * 1024 * 1024), file.size);
  //       const partBlob = file.slice(start, end);
  //       const partNumber = i + 1;

  //       const urlObj = urlsData.urls.find(p => p.part_number === partNumber);
  //       if (!urlObj) {
  //         throw new Error(`No URL found for part ${partNumber}`);
  //       }

  //       setCurrentStep(`Uploading part ${partNumber} of ${totalParts}...`);
  //       const etag = await uploadPart(urlObj.url, partBlob, partNumber);
  //       parts.push({ etag, part_number: partNumber });

  //       // Update progress
  //       const progress = Math.round(((i + 1) / totalParts) * 100);
  //       setUploadProgress(progress);
  //     }

  //     // 4. Complete the upload
  //     setCurrentStep('Finalizing upload...');
  //     const result = await completeMultipartUpload(fileMeta, parts);

  //     // 5. Process the file for GL accounts
  //     const parsedData = await parseFileForGlAccounts(file, glSystem);

  //     // 6. Call the bulk save API with the parsed data
  //     const token = getAuthToken();
  //     const tenantId = localStorage.getItem('tenant_id');

  //     const payload = Array.isArray(parsedData) ? parsedData : [parsedData];

  //     const response = await fetch(
  //       `${process.env.REACT_APP_BACKEND_URL}conductor/gl-sync/bulk-save-gl-accounts?tenantId=${tenantId}`,
  //       {
  //         method: 'POST',
  //         headers: {
  //           'Content-Type': 'application/json',
  //           'Authorization': `Bearer ${token}`,
  //         },
  //         // body: JSON.stringify(parsedData),
  //         body: JSON.stringify(payload),
  //       }
  //     );

  //     if (!response.ok) {
  //       const errorData = await response.json();
  //       throw new Error(errorData.message || 'Failed to save GL accounts');
  //     }

  //     const saveResult = await response.json();
  //     setUploadProgress(100);
  //     setSaveSuccess(true);
  //     setUploadDialogOpen(false);
  //     setUploadFile(null);
  //     setSelectedGlSystem('');
  //     setSelectedDocumentType(documentTypes[0]?.id || '');

  //     console.log('GL accounts saved successfully:', saveResult);
  //   } catch (error) {
  //     console.error('Error in handleBulkSaveGlAccounts:', error);
  //     setUploadError(error.message || 'Failed to process file');
  //     setSaveSuccess(false);
  //   } finally {
  //     setUploading(false);
  //     setCurrentStep('');
  //   }
  // };

  /** Build user-facing text from POST cash-recon/chart-of-account/bulk response */
  const formatChartOfAccountBulkToast = (body) => {
    if (!body || typeof body !== 'object') {
      return 'Chart of accounts processed.';
    }
    if (typeof body.message === 'string' && body.message.trim()) {
      return body.message.trim();
    }
    const created = body.createdCount ?? 0;
    const updated = body.updatedCount ?? 0;
    const skipped = body.skippedCount ?? 0;
    const saved = body.savedCount ?? created + updated;
    return `Saved ${saved} (${created} created, ${updated} updated); skipped ${skipped}.`;
  };

  /** Minimal body for POST conductor/cash-recon/chart-of-account/bulk */
  const buildChartOfAccountBulkRows = (parsedRows, tenantId, glSystemName, glHeaderId, propertyId) => {
    const glSys =
      normalizeGlSystemQueryParam(glSystemName) || String(glSystemName || '').trim().toUpperCase();
    const withOptional = (out, key, value) => {
      if (value == null) return;
      if (typeof value === 'string' && value.trim() === '') return;
      out[key] = value;
    };
    return parsedRows.map((row) => {
      const glCode1 = String(row.glCode1 ?? row.glCode ?? '').trim();
      const glCode2Raw = String(row.glCode2 ?? '').trim();
      const glCode2 = glCode2Raw || null;
      const glCode =
        glCode1 && glCode2Raw ? `${glCode1}.${glCode2Raw}` : (glCode1 || glCode2Raw || String(row.glCode || '').trim());
      const glDescription = String(row.glDescription ?? '').trim();
      const glAccount = String(row.glAccount ?? '').trim() || glCode;
      const out = {
        tenantId,
        glHeaderId,
        propertyId,
        glCode1,
        glCode2,
        glCode,
        glDescription,
        glAccount,
        glSystem: glSys,
        systemType: 'GL_SYNC',
      };
      withOptional(out, 'accountType', row.accountType);
      withOptional(out, 'transactionType', row.transactionType);
      withOptional(out, 'accountSubType', row.accountSubType);
      withOptional(out, 'normalBalance', row.normalBalance);
      withOptional(out, 'groupCode', row.groupCode);
      withOptional(out, 'currency', row.currency);
      withOptional(out, 'glJoinField', row.glJoinField);
      if (
        row.additionalAttributes &&
        typeof row.additionalAttributes === 'object' &&
        Object.keys(row.additionalAttributes).length > 0
      ) {
        out.additionalAttributes = row.additionalAttributes;
      }
      return out;
    });
  };

  const handleBulkSaveGlAccounts = async (file, glSystem) => {
    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    setCurrentStep('Validating file...');

    try {
      const token = getAuthToken();
      const tenantId = localStorage.getItem('tenant_id');
      const userId = localStorage.getItem('user_id');

      if (!tenantId) {
        throw new Error('Tenant ID not found. Please sign in again.');
      }
      if (!selectedDocumentType) {
        throw new Error('Please select a document type.');
      }

      const rawPropertyId =
        localStorage.getItem('property_id') || localStorage.getItem('selected_property_id') || '';
      let propertyIdForApi =
        rawPropertyId && String(rawPropertyId).trim() !== ''
          ? String(rawPropertyId).trim()
          : null;
      if (!propertyIdForApi) {
        propertyIdForApi = await fetchDefaultCashReconPropertyId(tenantId, token);
      }

      setCurrentStep('Processing file for GL accounts...');
      if (glAccountUploadMandatoryMessage) {
        throw new Error(glAccountUploadMandatoryMessage);
      }
      const combinationForParse =
        uploadColumnCombination.length >= GL_UPLOAD_COMBINATION_MIN &&
          uploadColumnCombination.length <= GL_UPLOAD_COMBINATION_MAX
          ? uploadColumnCombination
          : null;
      const useCombinationPath = Boolean(combinationForParse);

      let parsedData;
      const useRefTemplateUpload =
        Boolean(selectedUploadTemplate) &&
        !useCombinationPath &&
        uploadTemplates.some((t) => t.id === selectedUploadTemplate);

      if (useRefTemplateUpload) {
        const tmpl = activeUploadTemplate;
        if (!tmpl) {
          throw new Error('Selected template is not available. Choose a template again.');
        }
        const activeFieldCount = Object.values(tmpl.mappings || {}).filter(
          (spec) => spec && spec.isActive !== false
        ).length;
        if (!activeFieldCount) {
          throw new Error(
            'The selected template has no active fields. Pick another template or use canonical headers.'
          );
        }
        for (const [templateKeyRaw, spec] of Object.entries(tmpl.mappings || {})) {
          const templateKey = String(templateKeyRaw).trim();
          if (!spec || spec.isActive === false) continue;
          const pick = uploadExcelColumnByTemplateField[templateKey];
          if (!pick || String(pick).trim() === '') {
            throw new Error(
              `Pick a spreadsheet column for template field "${templateKey}" (Excel header "${String(spec.targetColumn || templateKey).trim()}").`
            );
          }
        }
        const { data: sheetRows } = await extractGlAccountFieldsFromSheet(file);
        if (!Array.isArray(sheetRows) || sheetRows.length === 0) {
          throw new Error('No data rows found in the spreadsheet.');
        }
        const mapped = applyGlAccountRefTemplateToRows(
          sheetRows,
          tmpl,
          uploadExcelColumnByTemplateField
        );
        const excelHeadersLower = getTemplateMappedExcelHeadersLower(tmpl, uploadExcelColumnByTemplateField);
        const canonicalUpper = getTemplateMappedCanonicalUpperSet(
          tmpl,
          uploadExcelColumnByTemplateField,
          resolveGlAccountTemplateFieldCanonical
        );
        parsedData = mapped.map((row) => {
          const base = glAccountMappedObjectsToParsedRows([row], glSystem)[0];
          const addl = buildAdditionalAttributesFromTemplateRow(row, excelHeadersLower, canonicalUpper);
          return addl ? { ...base, additionalAttributes: addl } : base;
        });
        const badIdx = parsedData.findIndex(
          (pr) =>
            !String(pr.glAccount || '').trim() ||
            !String(pr.glDescription || '').trim() ||
            !(String(pr.glCode1 ?? '').trim() || String(pr.glCode2 ?? '').trim())
        );
        if (badIdx !== -1) {
          throw new Error(
            `After template mapping, row ${badIdx + 1} is missing GL code, GL description, or GL account. Adjust the template mapping or spreadsheet.`
          );
        }
      } else {
        parsedData = await parseFileForGlAccounts(file, glSystem, combinationForParse);
      }
      const rowsRaw = Array.isArray(parsedData) ? parsedData : [parsedData];
      const rows = rowsRaw.map(compactGlAccountRowForUpload);

      const fileNameLower = String(file?.name || '').toLowerCase();
      const isExcelFile = fileNameLower.endsWith('.xlsx') || fileNameLower.endsWith('.xls');
      const isCsvFile = fileNameLower.endsWith('.csv');
      const isSpreadsheetFile = isExcelFile || isCsvFile;
      let fileForUpload = file;
      if (isSpreadsheetFile) {
        const rowsForS3 = rows.map(buildGlAccountRowForS3NativeKeys);
        const mappedJson = JSON.stringify(rowsForS3, null, 2);
        const jsonBlob = new Blob([mappedJson], { type: 'application/json' });
        const jsonFileName = file.name.replace(/\.(xlsx|xls|csv)$/i, '.json');
        fileForUpload = new File([jsonBlob], jsonFileName, { type: 'application/json' });
      }

      // 1) File pipeline: unit of work → multipart upload → complete (document id + file name)
      setCurrentStep('Starting file upload...');
      const uowData = await createUnitOfWork(fileForUpload);

      if (!uowData.documents || !Array.isArray(uowData.documents)) {
        throw new Error('Invalid unit of work response: missing documents array');
      }

      const fileMeta = uowData.documents.find((doc) => doc?.name === fileForUpload.name);
      if (!fileMeta) {
        throw new Error(`File metadata not found in unit of work response. Looking for: ${fileForUpload.name}`);
      }

      const urlsData = await getPresignedUrls(fileForUpload, uowData.id, fileMeta);

      const totalParts = Math.ceil(fileForUpload.size / (5 * 1024 * 1024));
      const parts = [];

      for (let i = 0; i < totalParts; i++) {
        const start = i * (5 * 1024 * 1024);
        const end = Math.min(start + (5 * 1024 * 1024), fileForUpload.size);
        const partBlob = fileForUpload.slice(start, end);
        const partNumber = i + 1;

        const urlObj = urlsData.urls.find((p) => p.part_number === partNumber);
        if (!urlObj) {
          throw new Error(`No URL found for part ${partNumber}`);
        }

        setCurrentStep(`Uploading part ${partNumber} of ${totalParts}...`);
        const etag = await uploadPart(urlObj.url, partBlob, partNumber);
        parts.push({ etag, part_number: partNumber });

        const progress = Math.round(((i + 1) / totalParts) * 100);
        setUploadProgress(progress);
      }

      setCurrentStep('Finalizing upload...');
      const uploadResult = await completeMultipartUpload(fileMeta, parts);

      const documentId = uploadResult?.id;
      const fileName = uploadResult?.name ?? fileForUpload.name;
      if (!documentId) {
        throw new Error('Upload completed but no document id was returned.');
      }

      const selectedDoc = documentTypes.find((d) => d.id === selectedDocumentType);
      const documentTypeLabel = selectedDoc?.name || String(selectedDocumentType);
      const notesTrimmed = uploadHeaderNotes.trim();

      // 2) Register GL header (uses document from multipart complete)
      setCurrentStep('Saving GL header...');
      /** Same pattern as tenant-system config: header token(s), e.g. `GL_CODE` or `GL_CODE.TRANSACTION_TYPE`. */
      const uploadSystemRow = findUploadSystemRow(systems, glSystem);
      const surrogateKeyForHeader = String(
        uploadSystemRow?.surrogateKey ?? uploadSystemRow?.surrogate_key ?? ''
      ).trim() || null;

      const targetGlSystem =
        selectedDestination && String(selectedDestination).trim() !== ''
          ? String(selectedDestination).trim().toUpperCase()
          : null;

      const glHeadersPayload = {
        tenantId,
        propertyId: propertyIdForApi,
        fundId: null,
        glSystem: (glSystem || '').toUpperCase(),
        targetGlSystem,
        glVersion: '1.0',
        systemType: 'GL_SYNC',
        documentType: documentTypeLabel,
        documentTypeId: selectedDocumentType,
        documentId,
        fileName,
        notes: notesTrimmed || null,
        createdBy: userId,
        updatedBy: userId,
        surrogateKey: surrogateKeyForHeader,
      };

      const glHeadersRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/gl-headers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(glHeadersPayload),
      });

      const glHeadersBody = await glHeadersRes.json().catch(() => ({}));
      if (!glHeadersRes.ok) {
        throw new Error(glHeadersBody.message || glHeadersBody.error || `GL header save failed: ${glHeadersRes.status}`);
      }

      const glHeaderId =
        glHeadersBody.glHeaderId ??
        glHeadersBody.id ??
        glHeadersBody.data?.glHeaderId ??
        glHeadersBody.data?.id;
      if (!glHeaderId) {
        throw new Error('GL header saved but the response did not include glHeaderId.');
      }

      // 3) Chart of accounts bulk (replaces conductor/gl-sync/bulk-save-gl-accounts)
      setCurrentStep('Saving chart of accounts...');
      const bulkRows = buildChartOfAccountBulkRows(rows, tenantId, glSystem, glHeaderId, propertyIdForApi);

      const bulkRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/chart-of-account/bulk`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(bulkRows),
        }
      );

      const bulkBody = await bulkRes.json().catch(() => ({}));
      if (!bulkRes.ok) {
        if (bulkRes.status === 400 && typeof bulkBody.message === 'string' && bulkBody.message.includes('Duplicate')) {
          throw new Error(bulkBody.message);
        }
        throw new Error(bulkBody.message || bulkBody.error || `Chart of accounts save failed: ${bulkRes.status}`);
      }

      setUploadProgress(100);
      showToast({
        message: formatChartOfAccountBulkToast(bulkBody),
        severity: 'success',
        autoHideDuration: 10000,
      });
      setUploadDialogOpen(false);
      setUploadFile(null);
      setSelectedGlSystem('');
      setUploadHeaderNotes('');
      setUploadFileHeaders([]);
      setUploadColumnCombination([]);
      setUploadSelectedSourceColumn(null);
      setSelectedDocumentType(documentTypes[0]?.id || '');

      console.log('Upload pipeline, gl-headers, and chart-of-account bulk completed:', {
        uploadResult,
        glHeaders: glHeadersBody,
        bulk: bulkBody,
      });
    } catch (error) {
      console.error('Error in handleBulkSaveGlAccounts:', error);

      if (error.message.includes('Failed to fetch')) {
        setUploadError('Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        setUploadError(error.message || 'An unexpected error occurred. Please try again.');
      }

      setSaveSuccess(false);
    } finally {
      setUploading(false);
      setCurrentStep('');
    }
  };


  // const parseFileForGlAccounts = (file, glSystem) => {
  //   return new Promise((resolve, reject) => {
  //     const reader = new FileReader();

  //     reader.onload = async (event) => {
  //       try {
  //         const data = event.target.result;
  //         let parsedData = [];

  //         if (file.name.endsWith('.csv')) {
  //           const text = new TextDecoder().decode(data);
  //           const rows = text.split('\n').map(row => {
  //             const regex = /,(?=(?:(?:[^"]*"){2})*[^"]*$)/;
  //             return row.split(regex).map(cell => cell.replace(/^"|"$/g, '').trim());
  //           });

  //           const headers = rows[0].map(header => header.toUpperCase());
  //           const glCodeIndex = headers.indexOf('GL_CODE');
  //           const glDescriptionIndex = headers.indexOf('GL_DESCRIPTION');
  //           const glAccountIndex = headers.indexOf('GL_ACCOUNT');

  //           parsedData = rows.slice(1).filter(row => row.length >= 1 && row.some(cell => cell.trim() !== '')).map(row => ({
  //             glCode: glCodeIndex !== -1 ? (row[glCodeIndex] || '').toString() : '',
  //             glDescription: glDescriptionIndex !== -1 ? (row[glDescriptionIndex] || '').toString() : '',
  //             glAccount: glAccountIndex !== -1 ? (row[glAccountIndex] || '').toString() : '',
  //             glSystem: glSystem.toUpperCase(),
  //             glSystemVersion: "1.0"
  //           }));
  //         } else if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
  //           const workbook = XLSX.read(data, { type: 'array' });
  //           const sheet = workbook.Sheets[workbook.SheetNames[0]];
  //           const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });

  //           if (json.length > 0) {
  //             const headers = json[0].map(header => (header || '').toString().toUpperCase());
  //             const glCodeIndex = headers.indexOf('GL_CODE');
  //             const glDescriptionIndex = headers.indexOf('GL_DESCRIPTION');
  //             const glAccountIndex = headers.indexOf('GL_ACCOUNT');

  //             parsedData = json.slice(1)
  //               .filter(row => row && row.length > 0)
  //               .map(row => ({
  //                 glCode: glCodeIndex !== -1 ? (row[glCodeIndex] || '').toString() : '',
  //                 glDescription: glDescriptionIndex !== -1 ? (row[glDescriptionIndex] || '').toString() : '',
  //                 glAccount: glAccountIndex !== -1 ? (row[glAccountIndex] || '').toString() : '',
  //                 glSystem: glSystem.toUpperCase(),
  //                 glSystemVersion: "1.0"
  //               }));
  //           }
  //         }

  //         resolve(parsedData);
  //       } catch (error) {
  //         reject(error);
  //       }
  //     };

  //     reader.onerror = (error) => {
  //       reject(new Error('Error reading file'));
  //     };

  //     if (file.name.endsWith('.csv')) {
  //       reader.readAsArrayBuffer(file);
  //     } else if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
  //       reader.readAsArrayBuffer(file);
  //     } else {
  //       reject(new Error('Unsupported file type'));
  //     }
  //   });
  // };

  const parseFileForGlAccounts = (file, glSystem, combinationPair) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const requiredHeaders = ['GL_CODE', 'GL_DESCRIPTION', 'GL_ACCOUNT'];
      const useCombination =
        Array.isArray(combinationPair) &&
        combinationPair.length >= GL_UPLOAD_COMBINATION_MIN &&
        combinationPair.length <= GL_UPLOAD_COMBINATION_MAX;

      reader.onload = async (event) => {
        try {
          const data = event.target.result;
          let parsedData = [];

          const mapDataRow = (
            row,
            headerRow,
            glCode1Index,
            glCode2Index,
            glDescriptionIndex,
            glAccountIndex,
            accountTypeIndex,
            headerIndexMap
          ) => {
            const glCode1 = (row[glCode1Index] || '').toString().trim();
            const glCode2 =
              glCode2Index >= 0 ? (row[glCode2Index] || '').toString().trim() : '';
            const accountType =
              accountTypeIndex >= 0 ? (row[accountTypeIndex] || '').toString().trim() : '';
            const optionalByIdx = (aliases) => {
              const idx = aliases
                .map((a) => headerIndexMap[String(a).trim().toUpperCase()])
                .find((v) => v != null && v >= 0);
              if (idx == null || idx < 0) return null;
              const raw = row[idx];
              if (raw == null) return null;
              const s = String(raw).trim();
              return s === '' ? null : s;
            };
            const glDescription = (row[glDescriptionIndex] || '').toString().trim();
            const glAccount = (row[glAccountIndex] || '').toString().trim();
            const base = {
              glCode1,
              glCode2,
              glDescription,
              glAccount,
              ...(glCode2 ? { transactionType: glCode2 } : {}),
              ...(accountType ? { accountType } : {}),
              accountSubType: optionalByIdx(['ACCOUNT_SUB_TYPE']),
              normalBalance: optionalByIdx(['NORMAL_BALANCE']),
              groupCode: optionalByIdx(['GROUP_CODE']),
              currency: optionalByIdx(['CURRENCY']),
              transactionType: optionalByIdx(['TRANSACTION_TYPE']) || (glCode2 || null),
              glJoinField: optionalByIdx(['GL_JOIN_FIELD']),
              glSystem: glSystem.toUpperCase(),
              glSystemVersion: '1.0',
            };
            if (headerIndexMap && Array.isArray(headerRow) && headerRow.length) {
              const addl = buildAdditionalAttributesFromHeaderIndexRow(row, headerRow, headerIndexMap);
              if (addl) base.additionalAttributes = addl;
            }
            return base;
          };

          const resolveHeaderIndices = (headerRow) => {
            const missingHeaders = requiredHeaders.filter((header) => !headerRow.includes(header));
            if (missingHeaders.length > 0) {
              throw new Error(
                `Missing required headers: ${missingHeaders.join(', ')}. Headers must match exactly (case-sensitive).`
              );
            }
            const glCode1Index = headerRow.indexOf('GL_CODE');
            const glDescriptionIndex = headerRow.indexOf('GL_DESCRIPTION');
            const glAccountIndex = headerRow.indexOf('GL_ACCOUNT');
            const glCode2Index = headerRow.findIndex(
              (h) => String(h || '').trim().toUpperCase() === 'TRANSACTION_TYPE'
            );
            const accountTypeIndex = headerRow.findIndex(
              (h) => String(h || '').trim().toUpperCase() === 'ACCOUNT_TYPE'
            );
            return { glCode1Index, glCode2Index, glDescriptionIndex, glAccountIndex, accountTypeIndex };
          };

          const buildHeaderIndexMap = (headerRow) => {
            const map = {};
            headerRow.forEach((h, idx) => {
              const key = String(h || '').trim().toUpperCase();
              if (key && map[key] == null) map[key] = idx;
            });
            return map;
          };

          const joinSecondSegmentFromRow = (row, restIndices) =>
            restIndices
              .map((idx) => (row[idx] || '').toString().trim())
              .filter(Boolean)
              .join('.');

          const mapCombinationRows = (bodyRows, headerRow) => {
            const glDescriptionIndex = headerRow.indexOf('GL_DESCRIPTION');
            const glAccountIndex = headerRow.indexOf('GL_ACCOUNT');
            const headerIndexMap = buildHeaderIndexMap(headerRow);
            const optionalByIdx = (row, aliases) => {
              const idx = aliases
                .map((a) => headerIndexMap[String(a).trim().toUpperCase()])
                .find((v) => v != null && v >= 0);
              if (idx == null || idx < 0) return null;
              const raw = row[idx];
              if (raw == null) return null;
              const s = String(raw).trim();
              return s === '' ? null : s;
            };
            if (glDescriptionIndex < 0 || glAccountIndex < 0) {
              throw new Error(
                'GL_DESCRIPTION and GL_ACCOUNT columns are required in the file when using combination mapping.'
              );
            }
            const indices = combinationPair.map((c) => c.index);
            const i0 = indices[0];
            const rest = indices.slice(1);
            const colCount = headerRow.length;
            const maxIdx = Math.max(...indices, glDescriptionIndex, glAccountIndex);
            if (indices.some((i) => i < 0) || maxIdx >= colCount) {
              throw new Error('Mapped column index is outside the file header row.');
            }
            const uniq = new Set(indices);
            if (uniq.size !== indices.length) {
              throw new Error('Combination columns must be distinct.');
            }
            const accountTypeIndex = headerRow.findIndex(
              (h) => String(h || '').trim().toUpperCase() === 'ACCOUNT_TYPE'
            );
            const transactionTypeIndex = headerRow.findIndex(
              (h) => String(h || '').trim().toUpperCase() === 'TRANSACTION_TYPE'
            );
            const consumedIndices = new Set(indices);
            consumedIndices.add(glDescriptionIndex);
            consumedIndices.add(glAccountIndex);
            if (transactionTypeIndex >= 0) consumedIndices.add(transactionTypeIndex);
            if (accountTypeIndex >= 0) consumedIndices.add(accountTypeIndex);
            ['ACCOUNT_SUB_TYPE', 'NORMAL_BALANCE', 'GROUP_CODE', 'CURRENCY', 'GL_JOIN_FIELD'].forEach((alias) => {
              const ix = headerIndexMap[alias];
              if (ix != null && ix >= 0) consumedIndices.add(ix);
            });
            return bodyRows.map((row) => {
              const transactionType =
                transactionTypeIndex >= 0 ? (row[transactionTypeIndex] || '').toString().trim() : '';
              const accountType =
                accountTypeIndex >= 0 ? (row[accountTypeIndex] || '').toString().trim() : '';
              const base = {
                glCode1: (row[i0] || '').toString().trim(),
                glCode2: joinSecondSegmentFromRow(row, rest),
                glDescription: (row[glDescriptionIndex] || '').toString().trim(),
                glAccount: (row[glAccountIndex] || '').toString().trim(),
                ...(transactionType ? { transactionType } : {}),
                ...(accountType ? { accountType } : {}),
                accountSubType: optionalByIdx(row, ['ACCOUNT_SUB_TYPE']),
                normalBalance: optionalByIdx(row, ['NORMAL_BALANCE']),
                groupCode: optionalByIdx(row, ['GROUP_CODE']),
                currency: optionalByIdx(row, ['CURRENCY']),
                glJoinField: optionalByIdx(row, ['GL_JOIN_FIELD']),
                glSystem: glSystem.toUpperCase(),
                glSystemVersion: '1.0',
              };
              const addl = buildAdditionalAttributesFromCombinationRow(row, headerRow, consumedIndices);
              if (addl) base.additionalAttributes = addl;
              return base;
            });
          };

          if (file.name.endsWith('.csv')) {
            const text = new TextDecoder().decode(data);
            const rows = text.split('\n').map((row) => {
              const regex = /,(?=(?:(?:[^"]*"){2})*[^"]*$)/;
              return row.split(regex).map((cell) => cell.replace(/^"|"$/g, '').trim());
            });

            const headerRow = rows[0] || [];
            const bodyRows = rows
              .slice(1)
              .filter((row) => row.length >= 1 && row.some((cell) => cell.trim() !== ''));

            if (useCombination) {
              parsedData = mapCombinationRows(bodyRows, headerRow);
            } else {
              const headerIndexMap = buildHeaderIndexMap(headerRow);
              const { glCode1Index, glCode2Index, glDescriptionIndex, glAccountIndex, accountTypeIndex } =
                resolveHeaderIndices(headerRow);
              parsedData = bodyRows.map((row) =>
                mapDataRow(
                  row,
                  headerRow,
                  glCode1Index,
                  glCode2Index,
                  glDescriptionIndex,
                  glAccountIndex,
                  accountTypeIndex,
                  headerIndexMap
                )
              );
            }
          } else if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
            const workbook = XLSX.read(data, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });

            if (json.length === 0) {
              throw new Error('The Excel file is empty.');
            }

            const headerRow = json[0].map((header) => (header || '').toString());
            const bodyRows = json.slice(1).filter((row) => row && row.length > 0);

            if (useCombination) {
              parsedData = mapCombinationRows(bodyRows, headerRow);
            } else {
              const headerIndexMap = buildHeaderIndexMap(headerRow);
              const { glCode1Index, glCode2Index, glDescriptionIndex, glAccountIndex, accountTypeIndex } =
                resolveHeaderIndices(headerRow);
              parsedData = bodyRows.map((row) =>
                mapDataRow(
                  row,
                  headerRow,
                  glCode1Index,
                  glCode2Index,
                  glDescriptionIndex,
                  glAccountIndex,
                  accountTypeIndex,
                  headerIndexMap
                )
              );
            }
          } else {
            throw new Error('Unsupported file type. Please upload a .csv, .xls, or .xlsx file.');
          }

          if (parsedData.length === 0) {
            throw new Error('No valid data found in the file. Please check the file contents.');
          }

          resolve(parsedData);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = (error) => {
        reject(new Error('Error reading file: ' + error.message));
      };

      if (file.name.endsWith('.csv') || file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
        reader.readAsArrayBuffer(file);
      } else {
        reject(new Error('Unsupported file type. Please upload a .csv, .xls, or .xlsx file.'));
      }
    });
  };



  // Function to fetch GL accounts from API (legacy function for backward compatibility)
  const fetchGlAccounts = async (systemName) => {
    setLoadingGlAccounts(true);
    try {
      const data = await fetchGlAccountsForSystem(systemName);

      if (data) {
        // Populate both source mapping data and destination data
        // Source data for mapping table
        setMappingData(data.map(item => ({
          source: item.glSystem,
          systemName: item.glSystem,
          glCode: item.glCode,
          glDescription: item.glDescription,
          glAccount: item.glAccount,
          destinationField: '-- No Mapping --',
          highlighted: false,
        })));

        // Destination data for dropdown options
        setDestinationData(data);
        setDestinationOptions(['-- No Mapping --', ...data.map((item) => formatGlCodeDescriptionLabel(item))]);
      }
    } catch (error) {
      console.error('Error in fetchGlAccounts:', error);
      setError(`Failed to load GL accounts: ${error.message}`);
    } finally {
      setLoadingGlAccounts(false);
    }
  };

  // Function to handle GL system selection and fetch data
  const handleGlSystemSelection = async (systemName, isSource) => {
    if (!systemName) {
      if (isSource) {
        setSelectedSource('');
        setMappingData([]);
      } else {
        setSelectedDestination('');
        setDestinationData([]);
        setDestinationOptions(['-- No Mapping --']);
      }
      return;
    }

    if (isSource) {
      setSelectedSource(systemName);
      // Clear only source data when changing source
      setMappingData([]);

      // Fetch source GL accounts immediately
      setLoadingGlAccounts(true);
      try {
        const sourceData = await fetchGlAccountsForSystem(systemName, {
          fundFilter: true,
          fundId: selectedFund?.id || null,
        });
        if (sourceData) {
          setMappingData(sourceData.map(item => ({
            source: item.glSystem,
            systemName: item.glSystem,
            glCode: item.glCode,
            glDescription: item.glDescription,
            glAccount: item.glAccount,
            destinationField: '-- No Mapping --',
            highlighted: false,
          })));
        }
      } catch (error) {
        console.error('Error fetching source GL accounts:', error);
        setError(`Failed to load source GL accounts: ${error.message}`);
      } finally {
        setLoadingGlAccounts(false);
      }
    } else {
      setSelectedDestination(systemName);
      // Clear only destination data when changing destination
      setDestinationData([]);
      setDestinationOptions(['-- No Mapping --']);

      // Destination stays shared (all COA for that system — not fund-filtered)
      setLoadingGlAccounts(true);
      try {
        const destinationData = await fetchGlAccountsForSystem(systemName, { fundFilter: false });
        if (destinationData) {
          setDestinationData(destinationData);
          setDestinationOptions([
            '-- No Mapping --',
            ...destinationData.map((item) => formatGlCodeDescriptionLabel(item)),
          ]);
        }
      } catch (error) {
        console.error('Error fetching destination GL accounts:', error);
        setError(`Failed to load destination GL accounts: ${error.message}`);
      } finally {
        setLoadingGlAccounts(false);
      }
    }
  };

  const handleMappingChange = (index, newValue) => {
    const updatedMappingData = [...mappingData];
    updatedMappingData[index].destinationField = newValue;
    setMappingData(updatedMappingData);
  };

  const handleStepClick = (stepIndex) => {
    if (stepIndex === 1) {
      navigate('/gl-sync/trial-balance-compare', {
        state: {
          sourceGL,
          destinationGL,
          glSystem: stripGlSystemLabelForNav(sourceGL) || sourceGL,
          activeStep: 0 // Set to Comparison step (first step in trial-balance-compare)
        }
      });
    }
  };

  const handleBreadcrumbClick = (path) => {
    navigate(path);
  };


  const handleSave = async () => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('user_id');
    const username = localStorage.getItem('username');
    if (!token) {
      setError('No authentication token found');
      return;
    }

    const tenantId = localStorage.getItem('tenant_id');
    if (!tenantId) {
      setError('No tenant ID found. Please ensure you are properly authenticated.');
      return;
    }

    if (!selectedSource || !selectedDestination) {
      setError('Please select both source and destination GL systems.');
      return;
    }

    if (!hasAtLeastOneRowMapping) {
      setError('Select at least one account mapping before saving.');
      return;
    }

    setIsSavingMapping(true);
    try {
      const rawPropertyId =
        localStorage.getItem('property_id') || localStorage.getItem('selected_property_id') || '';
      let propertyIdForMappings =
        rawPropertyId && String(rawPropertyId).trim() !== ''
          ? String(rawPropertyId).trim()
          : null;
      if (!propertyIdForMappings) {
        propertyIdForMappings = await fetchDefaultCashReconPropertyId(tenantId, token);
      }

      // First API Call - mapping-headers API
      const fundCode = selectedFund?.fundCode || null;
      const fundName = selectedFund?.fundName || null;
      const fundId = selectedFund?.id || null;
      const defaultDesc = fundCode
        ? `${selectedSource.toUpperCase()} to ${selectedDestination.toUpperCase()} Mapping (${fundCode})`
        : `${selectedSource.toUpperCase()} to ${selectedDestination.toUpperCase()} Mapping`;
      const response1 = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/gl-sync/mapping-headers?tenantId=${tenantId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          sourceGlSystem: selectedSource.toUpperCase(),
          targetGlSystem: selectedDestination.toUpperCase(),
          fundId,
          fundCode,
          fundName,
          mappingDescription:
            String(mappingDescription || '').trim() || defaultDesc,
          glOutputView: null,
        }),
      });

      if (!response1.ok) {
        const errorData = await response1.json();
        throw new Error(errorData.message || 'Failed to save mapping header');
      }

      const result1 = await response1.json();
      console.log('Mapping header saved successfully:', result1);

      // Filter out any null mappings (invalid ones)
      const validMappings = mappingData.filter(item => item.destinationField !== '-- No Mapping --');

      // Log the mapping save action
      try {
        await AuditLoggers.glMappingSaved(
          selectedSource.toUpperCase(),
          selectedDestination.toUpperCase(),
          validMappings.length,
          {
            userId,
            username,
            mappingHeaderId: result1.id,
            sourceSystem: selectedSource.toUpperCase(),
            targetSystem: selectedDestination.toUpperCase(),
            mappingCount: validMappings.length,
            timestamp: new Date().toISOString()
          }
        );
      } catch (logError) {
        console.error('Error logging GL mapping save:', logError);
        // Don't block the save operation if logging fails
      }

      // Second API Call - FastAPI mapping details
      const mappingPayloads = mappingData
        .filter(item => item.destinationField !== '-- No Mapping --')
        .map((item) => {
          const targetItem = findDestinationRowBySelectValue(destinationData, item.destinationField);
          if (!targetItem) {
            setError(`No matching target GL code found for ${item.destinationField}`);
            return null;
          }
          // Add _GL suffix if not already present
          const fromGlSource = selectedSource.endsWith('_GL')
            ? selectedSource
            : `${selectedSource}_GL`;

          const toGlSource = selectedDestination.endsWith('_GL')
            ? selectedDestination
            : `${selectedDestination}_GL`;

          // return {
          //   nesfrowid: null,
          //   from_gl_source: selectedSource.toUpperCase(),
          //   from_gl_code: item.glCode,
          //   from_gl_description: item.glDescription,
          //   from_gl_account: item.glAccount,
          //   to_gl_source: selectedDestination.toUpperCase(),
          //   to_gl_code: targetItem.glCode,
          //   to_gl_description: targetItem.glDescription,
          //   to_gl_account: targetItem.glAccount
          // };
          return {
            nesfrowid: null,
            from_gl_source: fromGlSource.toUpperCase(),
            from_gl_code: item.glCode,
            from_gl_description: item.glDescription,
            from_gl_account: item.glAccount,
            to_gl_source: toGlSource.toUpperCase(),
            to_gl_code: targetItem.glCode,
            to_gl_description: targetItem.glDescription,
            to_gl_account: targetItem.glAccount
          };
        })
        .filter(item => item !== null);

      if (mappingPayloads.length === 0) {
        setError('No valid mappings to save.');
        return;
      }

      // Conductor fund-scoped mappings first (source of truth for multi-fund)
      const conductorGlAccountMappingsBulk = mappingPayloads.map((p) => {
        const row = {
          tenantId,
          propertyId: propertyIdForMappings ?? null,
          fundId: selectedFund?.id ? String(selectedFund.id) : null,
          fundCode: selectedFund?.fundCode || null,
          fundName: selectedFund?.fundName || null,
          createdBy: userId || null,
          updatedBy: userId || null,
          fromGlSource: p.from_gl_source,
          toGlSource: p.to_gl_source,
        };
        const add = (key, val) => {
          if (val == null) return;
          const s = String(val).trim();
          if (s !== '') row[key] = s;
        };
        add('fromGlCode', p.from_gl_code);
        add('fromGlDescription', p.from_gl_description);
        add('fromGlAccount', p.from_gl_account);
        add('toGlCode', p.to_gl_code);
        add('toGlDescription', p.to_gl_description);
        add('toGlAccount', p.to_gl_account);
        return row;
      });

      const response3 = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/gl-account-mappings/bulk`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(conductorGlAccountMappingsBulk),
        }
      );

      if (!response3.ok) {
        const errBody = await response3.json().catch(() => ({}));
        throw new Error(
          errBody.message ||
          errBody.error ||
          errBody.detail ||
          `GL account mappings bulk failed: ${response3.status}`
        );
      }

      const result3 = await response3.json().catch(() => []);
      console.log('Conductor gl-account-mappings/bulk saved:', result3);

      // Push same rows to Snowflake DEV_ADMIN.POSTGRES.GL_ACCOUNT_MAPPING (fund-scoped)
      const snowflakeTenantId = resolveSnowflakeTenantId();
      if (snowflakeTenantId) {
        const fromGlSource = toChartOfAccountGlSystem(selectedSource);
        const toGlSource = toChartOfAccountGlSystem(selectedDestination);
        const pushUrl = new URL(
          `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/')}fastapi/v1/gl-sync/gl-account-mappings/sync-to-snowflake`
        );
        pushUrl.searchParams.set('snowflake_tenant_id', snowflakeTenantId);
        pushUrl.searchParams.set('tenant_uuid', tenantId);
        if (fromGlSource) pushUrl.searchParams.set('from_gl_source', fromGlSource);
        if (toGlSource) pushUrl.searchParams.set('to_gl_source', toGlSource);
        pushUrl.searchParams.set('fund_filter', 'true');
        if (fundCode) pushUrl.searchParams.set('fund_code', fundCode);

        const pushRes = await fetch(pushUrl.toString(), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        });
        if (!pushRes.ok) {
          const errBody = await pushRes.json().catch(() => ({}));
          const detail =
            errBody.detail ||
            errBody.message ||
            `Failed to push mappings to Snowflake GL_ACCOUNT_MAPPING (${pushRes.status})`;
          console.warn('Snowflake GL_ACCOUNT_MAPPING push warning (Postgres save already OK):', detail);
          // Do not fail the whole save — fund-scoped rows are already in Postgres
        } else {
          const pushBody = await pushRes.json().catch(() => ({}));
          console.log('Snowflake GL_ACCOUNT_MAPPING push OK:', pushBody);
        }
      } else {
        console.warn('No tenant_integration — skipped Snowflake GL_ACCOUNT_MAPPING push');
      }

      setSaveSuccess(true);

    } catch (err) {
      setError(`Error saving mapping: ${err.message}`);
    } finally {
      setIsSavingMapping(false);
    }
  };
  return (
    <Box sx={{
      ...harmonatePageSx,
      ...harmonateFormFieldsSx,
      height: '100vh',
    }}>
      {/* Error and Success Snackbars */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar open={saveSuccess} autoHideDuration={6000} onClose={() => setSaveSuccess(false)}>
        <Alert severity="success" onClose={() => setSaveSuccess(false)}>
          Mappings saved successfully!
        </Alert>
      </Snackbar>

      {/* Breadcrumbs Navigation */}
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" sx={{ color: ht.white }} />}
          sx={{
            mb: 1,
            '& .MuiBreadcrumbs-ol': {
              alignItems: 'center',
            },
          }}
        >
          <Link
            underline="hover"
            onClick={() => handleBreadcrumbClick('/gl-sync')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: ht.white,
              fontSize: '14px',
              fontWeight: 500,
              '&:hover': { color: ht.white },
            }}
          >
            <SyncIcon sx={{ mr: 0.5, fontSize: '16px' }} />
            GL Sync
          </Link>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            color: ht.white,
            fontSize: '14px',
            fontWeight: 600,
          }}>
            <MappingIcon sx={{ mr: 0.5, fontSize: '16px' }} />
            GL Mapping
          </Box>
        </Breadcrumbs>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          GL Mapping
        </Typography>
      </Box>

      {/* Upload GL Account white section — commented out; mapping buttons moved to Mapping systems row
      <Paper elevation={0} sx={{ ...harmonateCardSx, p: 3, mb: 3, bgcolor: ht.white, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: ht.text }}>
              Upload GL Account
            </Typography>
            <Typography variant="body2" sx={{ color: ht.text }}>
              Upload Excel files to create new GL Accounts
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button>Refresh Mapping</Button>
            <Button>Push Mapping</Button>
            <Button>Upload Files</Button>
          </Box>
        </Box>
      </Paper>
      */}

      {/* GL System Selection — white oval fields on page gradient */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          flexShrink: 0,
          ...harmonateGradientFormFieldsSx,
        }}
      >
        <Typography variant="subtitle2" sx={{ color: ht.white, fontWeight: 600 }}>
          Mapping systems
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: { xs: 'stretch', sm: 'center' },
            flexWrap: 'wrap',
          }}
        >
          <FormControl
            size="small"
            sx={{
              flex: { xs: '1 1 auto', sm: '0 1 20%' },
              minWidth: { xs: 0, sm: 140 },
              maxWidth: { xs: 'none', sm: 260 },
            }}
          >
            <Autocomplete
              fullWidth
              size="small"
              disabled={loadingSystems}
              componentsProps={glSyncSystemAutocompleteComponentsProps}
              options={systems}
              getOptionLabel={(option) => {
                const name = option?.name != null ? String(option.name) : '';
                return name;
              }}
              value={systems.find((s) => s.name === selectedSource) || null}
              onChange={(_, newValue) => {
                void handleGlSystemSelection(newValue?.name || '', true);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Source GL"
                  size="small"
                  placeholder="Select Source GL"
                  sx={{ minWidth: 0 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingSystems ? (
                          <CircularProgress size={20} sx={{ color: ht.white }} />
                        ) : (
                          params.InputProps.endAdornment
                        )}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option?.id != null && value?.id != null
                  ? option.id === value.id
                  : option?.name === value?.name
              }
              sx={{ fontSize: '14px' }}
            />
          </FormControl>
          <FormControl
            size="small"
            sx={{
              flex: { xs: '1 1 auto', sm: '0 1 22%' },
              minWidth: { xs: 0, sm: 160 },
              maxWidth: { xs: 'none', sm: 280 },
            }}
          >
            <Autocomplete
              fullWidth
              size="small"
              disabled={loadingFunds}
              componentsProps={glSyncSystemAutocompleteComponentsProps}
              options={funds}
              getOptionLabel={(option) => {
                if (!option || typeof option === 'string') return '';
                const name = option.fundName || '';
                const code = option.fundCode || '';
                if (name && code) return `${name} (${code})`;
                return name || code || '';
              }}
              value={selectedFund}
              onChange={(_, newValue) => {
                setSelectedFund(newValue);
              }}
              clearOnBlur={false}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Fund (optional)"
                  size="small"
                  placeholder="Select fund"
                  sx={{ minWidth: 0 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingFunds ? (
                          <CircularProgress size={20} sx={{ color: ht.white }} />
                        ) : (
                          params.InputProps.endAdornment
                        )}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option?.id != null && value?.id != null
                  ? String(option.id) === String(value.id)
                  : option?.fundCode === value?.fundCode
              }
              noOptionsText={loadingFunds ? 'Loading funds…' : 'No funds found'}
              sx={{ fontSize: '14px' }}
            />
          </FormControl>
          <FormControl
            size="small"
            sx={{
              flex: { xs: '1 1 auto', sm: '0 1 20%' },
              minWidth: { xs: 0, sm: 140 },
              maxWidth: { xs: 'none', sm: 260 },
            }}
          >
            <Autocomplete
              fullWidth
              size="small"
              disabled={loadingSystems}
              componentsProps={glSyncSystemAutocompleteComponentsProps}
              options={systems}
              getOptionLabel={(option) => {
                const name = option?.name != null ? String(option.name) : '';
                return name;
              }}
              value={systems.find((s) => s.name === selectedDestination) || null}
              onChange={(_, newValue) => {
                void handleGlSystemSelection(newValue?.name || '', false);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Destination GL"
                  size="small"
                  placeholder="Select Destination GL"
                  sx={{ minWidth: 0 }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingSystems ? (
                          <CircularProgress size={20} sx={{ color: ht.white }} />
                        ) : (
                          params.InputProps.endAdornment
                        )}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option?.id != null && value?.id != null
                  ? option.id === value.id
                  : option?.name === value?.name
              }
              sx={{ fontSize: '14px' }}
            />
          </FormControl>
          <FormControl
            size="small"
            sx={{
              flex: { xs: '1 1 auto', sm: '1 1 0px' },
              minWidth: { xs: 0, sm: 200 },
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Mapping description"
              placeholder={
                selectedSource && selectedDestination
                  ? selectedFund?.fundCode
                    ? `${selectedSource.toUpperCase()} to ${selectedDestination.toUpperCase()} Mapping (${selectedFund.fundCode})`
                    : `${selectedSource.toUpperCase()} to ${selectedDestination.toUpperCase()} Mapping`
                  : 'Name this mapping (optional)'
              }
              value={mappingDescription}
              onChange={(e) => setMappingDescription(e.target.value)}
              sx={{ minWidth: 0 }}
            />
          </FormControl>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexShrink: 0,
              flexWrap: 'wrap',
            }}
          >
            <Tooltip title="Load GL account mappings from Snowflake (property_id ignored / null in Postgres)">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    refreshingMapping ? <CircularProgress size={16} /> : <CloudDownloadIcon />
                  }
                  onClick={() => setMappingConfirmAction('refresh')}
                  disabled={
                    refreshingMapping ||
                    pushingMapping ||
                    (!isHarmonateUser && !hasPermission('GL Sync', 'upload'))
                  }
                  sx={{
                    ...harmonateUploadOutlinedSx,
                    bgcolor: ht.white,
                    '&:hover': { bgcolor: ht.white },
                  }}
                >
                  {refreshingMapping ? 'Refreshing…' : 'Refresh Mapping'}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Push GL account mappings from Postgres to Snowflake (preserves Snowflake fund PROPERTY_ID)">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    pushingMapping ? <CircularProgress size={16} /> : <CloudUploadIcon />
                  }
                  onClick={() => setMappingConfirmAction('push')}
                  disabled={
                    refreshingMapping ||
                    pushingMapping ||
                    (!isHarmonateUser && !hasPermission('GL Sync', 'upload'))
                  }
                  sx={{
                    ...harmonateUploadOutlinedSx,
                    bgcolor: ht.white,
                    '&:hover': { bgcolor: ht.white },
                  }}
                >
                  {pushingMapping ? 'Pushing…' : 'Push Mapping'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Paper elevation={0} sx={{
          ...harmonateCardSx,
          bgcolor: ht.white,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Header Row */}
          {/* <Box sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1.5,
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #e9ecef',
          }}>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{
                fontWeight: 600,
                color: '#495057',
                mr: 2,
                fontSize: '15px',
              }}>
                {selectedSource ? `${selectedSource} GL` : 'Source GL'}
              </Typography>
            </Box>

            <Typography variant="subtitle1" sx={{
              fontWeight: 600,
              color: '#495057',
              flex: 1,
              textAlign: 'center',
              fontSize: '15px',
            }}>
              {selectedDestination ? `${selectedDestination} GL` : 'Destination GL'}
            </Typography>

            <Typography variant="subtitle1" sx={{
              fontWeight: 600,
              color: '#495057',
              flex: 1,
              textAlign: 'center',
              fontSize: '15px',
            }}>
              Suggested Match
            </Typography>

            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                disabled={isGlMappingSaveDisabled}
                sx={{
                  backgroundColor: isGlMappingSaveDisabled ? '#ccc' : '#8CD867',
                  '&:hover': {
                    backgroundColor: isGlMappingSaveDisabled ? '#ccc' : '#7BC857',
                  },
                  textTransform: 'none',
                  fontSize: '12px',
                  borderRadius: '6px',
                  px: 2.5,
                  py: 0.5,
                }}
                onClick={handleSave}
              >
                Save
              </Button>
            </Box>
          </Box> */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '2fr 1.5fr 1.5fr 1fr',
            alignItems: 'center',
            p: 1.5,
            backgroundColor: ht.listBg,
            borderBottom: `1px solid ${ht.line}`,
            gap: 2,
          }}>
            {/* Source GL Header */}
            <Typography variant="subtitle1" sx={{
              fontWeight: 600,
              color: ht.text,
              fontSize: '15px',
            }}>
              {selectedSource ? `${selectedSource} GL` : 'Source GL'}
            </Typography>

            {/* Destination GL Header */}
            <Typography variant="subtitle1" sx={{
              fontWeight: 600,
              color: ht.text,
              fontSize: '15px',
              textAlign: 'center',
            }}>
              {selectedDestination ? `${selectedDestination} GL` : 'Destination GL'}
            </Typography>

            {/* Suggested Match Header */}
            <Typography variant="subtitle1" sx={{
              fontWeight: 600,
              color: ht.text,
              fontSize: '15px',
              textAlign: 'center',
            }}>
              Suggested Match
            </Typography>

            {/* Save Button - Right-aligned */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                disabled={
                  aiMatching ||
                  loadingFuzzyMatches ||
                  !selectedSource ||
                  !selectedDestination ||
                  mappingData.length === 0 ||
                  destinationData.length === 0
                }
                onClick={handleAiMatching}
                sx={{
                  ...harmonateUploadOutlinedSx,
                  fontSize: '12px',
                  px: 2.5,
                  py: 0.5,
                }}
              >
                {aiMatching || loadingFuzzyMatches ? (
                  <>
                    <CircularProgress size={14} sx={{ color: 'inherit', mr: 1 }} />
                    Matching…
                  </>
                ) : (
                  'AI Matching'
                )}
              </Button>
              {/* <Button
                variant="contained"
                disabled={isGlMappingSaveDisabled}
                sx={harmonateProceedButtonSx}
                onClick={handleSave}
              >
                Save
              </Button> */}
              <Button
                variant="contained"
                disabled={isGlMappingSaveDisabled || isSavingMapping}
                sx={{
                  ...harmonateProceedButtonSx,
                  '&.Mui-disabled': isSavingMapping && !isGlMappingSaveDisabled
                    ? {
                      backgroundColor: ht.green,
                      color: ht.white,
                      opacity: 1,
                    }
                    : harmonateProceedButtonSx['&.Mui-disabled'],
                  fontSize: '12px',
                  px: 2.5,
                  py: 0.5,
                }}
                onClick={handleSave}
              >
                {isSavingMapping ? (
                  <>
                    <CircularProgress size={14} sx={{ color: ht.white, mr: 1 }} />
                    Saving…
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </Box>
          </Box>
          {/* Mapping Table */}
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            {loadingGlAccounts ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    Loading GL accounts...
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Table size="small" stickyHeader>
                {/* <TableBody>
                  {mappingData.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: row.highlighted ? '#e8f5e8' : 'transparent',
                        '&:hover': {
                          backgroundColor: row.highlighted ? '#d4edda' : '#f8f9fa',
                        },
                        borderBottom: '1px solid #e9ecef',
                      }}
                    >
                      <TableCell sx={{
                        width: '50%',
                        py: 1,
                        px: 2,
                        fontSize: '13px',
                        color: '#495057',
                        fontWeight: 500,
                      }}>
                        {formatGlCodeDescriptionLabel(row)}
                      </TableCell>
                      <TableCell sx={{
                        width: '50%',
                        py: 1,
                        px: 2,
                        fontSize: '13px',
                        color: '#495057',
                        fontWeight: 500,
                      }}>
                        {selectedDestination ? (
                          <FormControl size="small" fullWidth>
                            <Select
                              value={row.destinationField}
                              onChange={(e) => handleMappingChange(index, e.target.value)}
                              sx={{
                                fontSize: '13px',
                                '& .MuiSelect-select': {
                                  py: 0.5,
                                  px: 1,
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#e9ecef',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#ced4da',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: ht.green,
                                  borderWidth: 1,
                                },
                              }}
                            >
                              {destinationOptions.map((option) => (
                                <MenuItem
                                  key={option}
                                  value={option}
                                  sx={{
                                    fontSize: '13px',
                                    color: option === '-- No Mapping --' ? '#6c757d' : '#495057',
                                    fontStyle: option === '-- No Mapping --' ? 'italic' : 'normal',
                                  }}
                                >
                                  {option}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography sx={{
                            fontSize: '13px',
                            color: '#000000',
                            fontStyle: 'italic'
                          }}>
                            Select destination GL system
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody> */}
                <TableBody>
                  {mappingData.map((row, index) => {
                    const suggestedMatchRaw = fuzzyMatches[row.glCode];
                    const suggestedDestRow = findDestinationRowBySelectValue(
                      destinationData,
                      suggestedMatchRaw
                    );
                    const suggestedMatchLabel = suggestedDestRow
                      ? formatGlCodeDescriptionLabel(suggestedDestRow)
                      : suggestedMatchRaw;
                    const hasSuggestion =
                      !!suggestedMatchRaw && suggestedMatchRaw !== '-- No Mapping --';

                    const destinationSelectValue = (() => {
                      const v = row.destinationField;
                      if (!v || v === '-- No Mapping --') return '-- No Mapping --';
                      const dr = findDestinationRowBySelectValue(destinationData, v);
                      return dr ? formatGlCodeDescriptionLabel(dr) : v;
                    })();

                    return (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor: row.highlighted ? 'rgba(2, 107, 29, 0.08)' : 'transparent',
                          '&:hover': {
                            backgroundColor: row.highlighted ? 'rgba(2, 107, 29, 0.14)' : ht.listBg,
                          },
                          borderBottom: `1px solid ${ht.line}`,
                        }}
                      >
                        <TableCell sx={{
                          width: '30%',
                          py: 1,
                          px: 2,
                          fontSize: '13px',
                          color: ht.text,
                          fontWeight: 500,
                        }}>
                          {formatGlCodeDescriptionLabel(row)}
                        </TableCell>

                        <TableCell sx={{
                          width: '30%',
                          py: 1,
                          px: 2,
                          fontSize: '13px',
                          color: ht.text,
                          fontWeight: 500,
                        }}>
                          {selectedDestination ? (
                            <Autocomplete
                              size="small"
                              fullWidth
                              value={destinationSelectValue}
                              onChange={(event, newValue) =>
                                handleMappingChange(index, newValue || '-- No Mapping --')
                              }
                              options={destinationOptions}
                              freeSolo
                              disableClearable
                              selectOnFocus
                              handleHomeEndKeys
                              isOptionEqualToValue={(option, val) => option === val}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Search destination..."
                                  size="small"
                                  variant="outlined"
                                  fullWidth
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      fontSize: '13px',
                                      '& fieldset': {
                                        borderColor: ht.line,
                                      },
                                      '&:hover fieldset': {
                                        borderColor: ht.text,
                                      },
                                      '&.Mui-focused fieldset': {
                                        borderColor: ht.darkBlue,
                                        borderWidth: 1,
                                      },
                                    },
                                  }}
                                />
                              )}
                              renderOption={(props, option) => (
                                <Box
                                  component="li"
                                  {...props}
                                  sx={{
                                    fontSize: '13px',
                                    py: 1,
                                    color: option === '-- No Mapping --' ? ht.fadedText : ht.text,
                                    fontStyle: option === '-- No Mapping --' ? 'italic' : 'normal',
                                    '&:hover': {
                                      backgroundColor: ht.secondaryTab,
                                    },
                                  }}
                                >
                                  {option}
                                </Box>
                              )}
                            />
                          ) : (
                            <Typography sx={{
                              fontSize: '13px',
                              color: ht.fadedText,
                              fontStyle: 'italic'
                            }}>
                              Select destination GL system
                            </Typography>
                          )}
                        </TableCell>
                        {/* Suggested Match Column */}
                        <TableCell sx={{
                          width: '30%',
                          py: 1,
                          px: 2,
                          fontSize: '13px',
                          color: hasSuggestion ? ht.text : ht.fadedText,
                          fontStyle: hasSuggestion ? 'normal' : 'italic',
                        }}>
                          {hasSuggestion ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleUseSuggestedMatch(index)}
                                sx={{
                                  textTransform: 'none',
                                  fontSize: '11px',
                                  padding: '2px 8px',
                                  borderColor: ht.green,
                                  color: ht.green,
                                  borderRadius: ht.buttonRadius,
                                  '&:hover': {
                                    backgroundColor: 'rgba(2, 107, 29, 0.08)',
                                    borderColor: ht.green,
                                  },
                                }}
                              >
                                Use
                              </Button>
                              <span>
                                {suggestedMatchLabel || suggestedMatchRaw}
                                {aiMatchTypes[row.glCode] ? ` (${aiMatchTypes[row.glCode]})` : ''}
                              </span>
                            </Box>
                          ) : loadingFuzzyMatches ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={14} />
                              Loading…
                            </Box>
                          ) : (
                            'No suggestion'
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Paper>
      </Box>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => {
          setUploadDialogOpen(false);
          setUploadHeaderNotes('');
          setUploadFileHeaders([]);
          setUploadColumnCombination([]);
          setUploadSelectedSourceColumn(null);
          resetUploadTemplateUi();
        }}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          ...harmonateDialogPaperProps,
          sx: {
            ...harmonateDialogPaperProps.sx,
            maxHeight: 'min(920px, calc(100vh - 32px))',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ ...harmonateDialogTitleSx, flexShrink: 0 }}>
          Upload GL Account
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            ...harmonateDialogFormContentSx,
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          <DialogContentText sx={{ mb: 0, fontSize: '14px', color: ht.text }}>
            Upload your GL accounts data file and Select a GL system
          </DialogContentText>

          <Box>
            <HiddenInput
              accept=".csv,.xls,.xlsx"
              id="upload-file"
              type="file"
              onChange={(e) => setUploadFile(e.target.files[0] || null)}
            />
            <label htmlFor="upload-file" style={{ display: 'block', marginBottom: '8px' }}>
              <UploadArea
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                  padding: '24px 16px',
                  border: isDragging ? `2px dashed ${ht.green}` : `2px dashed ${ht.line}`,
                  borderRadius: ht.fieldRadius,
                  backgroundColor: isDragging ? ht.secondaryTab : ht.listBg,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: ht.blue,
                    backgroundColor: ht.secondaryTab,
                  }
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 32, color: ht.green, mb: 1 }} />
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '13px', color: ht.text }}>
                  {isDragging ? 'Drop your file here' : 'Drop your file or browse'}
                </Typography>
                <Typography variant="caption" sx={{ color: ht.fadedText, fontSize: '11px' }}>
                  Max 10 MB files are allowed
                </Typography>
              </UploadArea>
            </label>

            {uploadFile && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mt: 2,
                  mb: 0,
                  backgroundColor: ht.listBg,
                  border: `1px solid ${ht.line}`,
                  borderRadius: ht.fieldRadius,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      backgroundColor: ht.secondaryTab,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <InsertDriveFileIcon sx={{ fontSize: 24, color: ht.text }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: ht.text,
                        fontSize: '14px',
                        mb: 0.5,
                      }}
                    >
                      {uploadFile.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#000000', fontSize: '12px' }}>
                      {(uploadFile.size / 1024).toFixed(2)} KB
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    setUploadFile(null);
                    setUploadProgress(0);
                    setUploadFileHeaders([]);
                    setUploadColumnCombination([]);
                    setUploadSelectedSourceColumn(null);
                    resetUploadTemplateUi();
                  }}
                  sx={{
                    color: '#000000',
                    '&:hover': { color: '#d32f2f', backgroundColor: '#ffebee' },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Paper>
            )}
            {uploadFile && !uploadHeadersLoading && glAccountUploadMandatoryMessage ? (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {glAccountUploadMandatoryMessage}
              </Alert>
            ) : null}

            <Typography variant="caption" sx={{ color: '#000000', fontSize: '10px', display: 'block', mt: 1.5 }}>
              Only support CSV and XLS/XLSX
            </Typography>

            {/* <Typography variant="subtitle2" sx={{ color: '#66BB6A', fontWeight: 600 }}>
            Upload details
          </Typography> */}

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                alignItems: 'stretch',
                mt: 1,
                mb: 1,
              }}
            >
              <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                <Autocomplete
                  fullWidth
                  size="small"
                  disabled={loadingSystems}
                  componentsProps={glSyncSystemAutocompleteComponentsProps}
                  options={systems}
                  getOptionLabel={(option) => {
                    const name = option?.name != null ? String(option.name) : '';
                    return name;
                  }}
                  value={findUploadSystemRow(systems, selectedGlSystem) || null}
                  onChange={(_, newValue) => {
                    setSelectedGlSystem(newValue?.name || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="GL System"
                      size="small"
                      placeholder="Select GL system"
                      sx={{ minWidth: 0 }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingSystems ? (
                              <CircularProgress size={20} />
                            ) : (
                              params.InputProps.endAdornment
                            )}
                          </>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option?.id != null && value?.id != null
                      ? option.id === value.id
                      : option?.name === value?.name
                  }
                  sx={{ fontSize: '14px' }}
                />
              </FormControl>
              {loadingDocumentTypes ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 1,
                    minHeight: 40,
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ ml: 1, fontSize: '12px' }}>
                    Loading document types...
                  </Typography>
                </Box>
              ) : selectedDocumentType ? (
                <TextField
                  label="Document Type"
                  value="GL Account"
                  size="small"
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1, minWidth: 0 }}
                />
              ) : (
                <Alert severity="warning" sx={{ flex: 1, py: 0.5 }}>
                  GL Account is not enabled for this tenant. Configure it in document type settings.
                </Alert>
              )}
            </Box>
            {selectedDocumentType && selectedGlSystem?.trim() ? (
              <Box sx={{ mt: 2 }}>
                {loadingUploadTemplates ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#000000' }}>
                      Loading ref-templates…
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 0.5,
                      }}
                    >
                      <FormControl size="small" fullWidth sx={{ flex: 1, minWidth: 0 }}>
                        <InputLabel id="gl-sync-mapping-upload-tpl-label">
                          Spreadsheet template (optional)
                        </InputLabel>
                        <Select
                          labelId="gl-sync-mapping-upload-tpl-label"
                          label="Spreadsheet template (optional)"
                          value={selectedUploadTemplate}
                          onChange={(e) => setSelectedUploadTemplate(String(e.target.value ?? ''))}
                          disabled={uploading}
                        >
                          <MenuItem value="">
                            <em>No template — canonical headers in row 1</em>
                          </MenuItem>
                          {uploadTemplates.map((template) => (
                            <MenuItem key={template.id} value={template.id}>
                              {template.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Tooltip title="Add or edit template fields (same as Reference Templates)">
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Edit reference template mappings"
                            disabled={
                              uploading ||
                              !selectedUploadTemplate ||
                              !activeUploadApiTemplateForEditor
                            }
                            onClick={() => setUploadTemplateMappingsEditorOpen(true)}
                            sx={{ mt: 0.5 }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                    {uploadTemplates.length === 0 ? (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mt: 0.75, color: '#6c757d', fontSize: '12px' }}
                      >
                        No ref-templates returned for GL Account / this system (
                        <code style={{ fontSize: '11px' }}>by-document-and-gl</code>
                        ). Use canonical headers.
                      </Typography>
                    ) : null}
                    {uploadCombinationPathActive ? (
                      <Alert severity="info" sx={{ mt: 1, py: 0.75, fontSize: '13px' }}>
                        Surrogate-key combination columns are active, so upload still follows the existing
                        combination logic. Template comparison remains visible for reference.
                      </Alert>
                    ) : null}
                    {selectedUploadTemplate && uploadFile && uploadTemplateColumnRows.length > 0 ? (
              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  p: 2,
                  border: `1px solid ${ht.line}`,
                  borderRadius: ht.fieldRadius,
                  bgcolor: ht.listBg,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: ht.text }}>
                  Template fields vs your file
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: ht.text }}>
                  Left: template logical field and the Excel header your file must provide (targetColumn). Right:
                  matching column from the uploaded sheet. Green when that header is present; red when it is missing.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" sx={{ color: ht.text }}>
                    Missing fields: {missingTemplateColumnCount}
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setShowMissingTemplateFields((prev) => !prev)}
                  >
                    {showMissingTemplateFields ? 'Show mapped fields' : 'Show missing fields'}
                  </Button>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 0,
                    border: `1px solid ${ht.line}`,
                    borderRadius: 1,
                    overflow: 'hidden',
                    maxHeight: 360,
                    overflowY: 'auto',
                    bgcolor: ht.white,
                  }}
                >
                  <Box sx={{ px: 1.5, py: 1, bgcolor: ht.listBg, borderBottom: `1px solid ${ht.line}`, fontSize: '12px', fontWeight: 600, color: ht.text }}>
                    Template required header
                  </Box>
                  <Box sx={{ px: 1.5, py: 1, bgcolor: ht.listBg, borderBottom: `1px solid ${ht.line}`, fontSize: '12px', fontWeight: 600, color: ht.text }}>
                    Column in your file
                  </Box>
                  {visibleUploadTemplateColumnRows.length === 0 ? (
                    <Box
                      sx={{
                        gridColumn: { xs: '1 / -1', sm: '1 / 3' },
                        px: 1.5,
                        py: 2,
                        fontSize: '13px',
                        color: '#6b7280',
                      }}
                    >
                      {showMissingTemplateFields
                        ? 'All required template fields are present.'
                        : 'No mapped fields found in your file.'}
                    </Box>
                  ) : null}
                  {visibleUploadTemplateColumnRows.map((row, idx) => (
                    <React.Fragment key={row.templateKey}>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1.25,
                          borderBottom:
                            idx < visibleUploadTemplateColumnRows.length - 1 ? '1px solid #f3f4f6' : 'none',
                          borderRight: { xs: 'none', sm: '1px solid #f3f4f6' },
                          fontSize: '13px',
                          color: row.matched ? '#166534' : '#b91c1c',
                          bgcolor: row.matched ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.06)',
                        }}
                      >
                        <Typography component="span" sx={{ fontWeight: 600, color: 'inherit' }}>
                          {row.templateKey}
                        </Typography>
                        <Typography component="span" sx={{ color: '#6b7280', mx: 0.75 }}>
                          →
                        </Typography>
                        <Typography component="span" sx={{ fontFamily: 'monospace', color: 'inherit' }}>
                          {row.expectedHeader}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1.25,
                          borderBottom:
                            idx < visibleUploadTemplateColumnRows.length - 1 ? '1px solid #f3f4f6' : 'none',
                          fontSize: '13px',
                          fontFamily: 'monospace',
                          color: row.matched ? '#166534' : '#b91c1c',
                          bgcolor: row.matched ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.06)',
                        }}
                      >
                        {row.displayFileCol}
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              </Paper>
                    ) : null}
                  </>
                )}
              </Box>
            ) : null}

            {uploadFile && (
              uploadHeadersLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 2,
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#000000' }}>
                    Reading column headers…
                  </Typography>
                </Box>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mt: 2,
                    mb: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Column mapping (optional)
                  </Typography>
                  {/* <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: '#000000' }}>
                      Choose two columns in order: their cell values are joined with a dot to form the saved account
                      code (same idea as <strong>GL_CODE</strong> plus optional <strong>TRANSACTION_TYPE</strong>).{' '}
                      <strong>GL_DESCRIPTION</strong> and <strong>GL_ACCOUNT</strong> are always read from those
                      headers. If you skip this mapping, the account code comes from <strong>GL_CODE</strong> and optional{' '}
                      <strong>TRANSACTION_TYPE</strong> as before.
                    </Typography> */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2,
                      alignItems: { xs: 'stretch', sm: 'flex-start' },
                      minHeight: 160,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Source columns
                      </Typography>
                      <List
                        dense
                        sx={{
                          maxHeight: 220,
                          overflow: 'auto',
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          bgcolor: '#fff',
                        }}
                      >
                        {uploadSourceColumnsAvailable.length === 0 ? (
                          <ListItem>
                            <ListItemText
                              primary={
                                uploadFileHeaders.length === 0 ? 'No headers found' : 'All columns mapped'
                              }
                            />
                          </ListItem>
                        ) : (
                          uploadSourceColumnsAvailable.map((col) => (
                            <ListItem key={col.index} disablePadding>
                              <ListItemButton
                                selected={uploadSelectedSourceColumn?.index === col.index}
                                onClick={() => setUploadSelectedSourceColumn(col)}
                              >
                                <ListItemText
                                  primary={
                                    col.name?.trim() ? col.name : `(Column ${col.index + 1})`
                                  }
                                />
                              </ListItemButton>
                            </ListItem>
                          ))
                        )}
                      </List>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: { xs: 0, sm: 2 },
                      }}
                    >
                      <IconButton
                        color="primary"
                        aria-label="Add selected column to combination"
                        disabled={
                          !uploadSelectedSourceColumn ||
                          uploadColumnCombination.length >= GL_UPLOAD_COMBINATION_MAX
                        }
                        onClick={() => {
                          if (
                            !uploadSelectedSourceColumn ||
                            uploadColumnCombination.length >= GL_UPLOAD_COMBINATION_MAX
                          ) {
                            return;
                          }
                          setUploadColumnCombination((prev) => [...prev, uploadSelectedSourceColumn]);
                          setUploadSelectedSourceColumn(null);
                        }}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        <ArrowForwardIcon />
                      </IconButton>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Combination columns
                      </Typography>
                      <List
                        dense
                        sx={{
                          maxHeight: 220,
                          overflow: 'auto',
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          bgcolor: '#fff',
                        }}
                      >
                        {uploadColumnCombination.length === 0 ? (
                          <ListItem>
                            <ListItemText
                              primary="None yet"
                              secondary="If the GL system surrogate key uses | between header names that exist in this file (e.g. GL_CODE|TRANSACTION_TYPE), columns auto-fill. Or add 1–5 source columns: first → primary code segment; extras → secondary segment built from those cells."
                            />
                          </ListItem>
                        ) : (
                          uploadColumnCombination.map((col, idx) => (
                            <ListItem
                              key={`${col.index}-${idx}`}
                              secondaryAction={
                                <IconButton
                                  edge="end"
                                  size="small"
                                  aria-label="Remove from combination"
                                  onClick={() =>
                                    setUploadColumnCombination((prev) => prev.filter((_, i) => i !== idx))
                                  }
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              }
                            >
                              <ListItemText
                                primary={
                                  col.name?.trim() ? col.name : `(Column ${col.index + 1})`
                                }
                              />
                            </ListItem>
                          ))
                        )}
                      </List>
                    </Box>
                  </Box>
                </Paper>
              )
            )}

          </Box>


          <TextField
            label="Description"
            name="uploadHeaderNotes"
            value={uploadHeaderNotes}
            onChange={(e) => setUploadHeaderNotes(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            disabled={uploading}
            InputLabelProps={{ shrink: true }}
            // helperText="Optional notes"
            FormHelperTextProps={{ sx: { mx: 0 } }}
          />

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {currentStep || 'Uploading...'} ({uploadProgress}%)
              </Typography>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}
          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadError}
            </Alert>
          )}

        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={() => {
              setUploadDialogOpen(false);
              setUploadFile(null);
              setSelectedGlSystem('');
              setUploadHeaderNotes('');
              setUploadFileHeaders([]);
              setUploadColumnCombination([]);
              setUploadSelectedSourceColumn(null);
              resetUploadTemplateUi();
              setUploadProgress(0);
              setUploadError(null);  // Clear any error messages
              setSaveSuccess(false); // Reset success state
            }}
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (selectedGlSystem && uploadFile && selectedDocumentType) {
                handleBulkSaveGlAccounts(uploadFile, selectedGlSystem);
              } else {
                setError('Please select a GL system, document type, and upload a file.');
              }
            }}
            variant="contained"
            disabled={
              !selectedGlSystem ||
              !uploadFile ||
              !selectedDocumentType ||
              uploading ||
              uploadHeadersLoading ||
              Boolean(glAccountUploadMandatoryMessage)
            }
            sx={harmonateProceedButtonSx}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      <RefTemplateMappingsEditorDrawer
        open={uploadTemplateMappingsEditorOpen}
        onClose={() => setUploadTemplateMappingsEditorOpen(false)}
        apiTemplate={activeUploadApiTemplateForEditor}
        getAuthToken={getAuthToken}
        persistToApi={false}
        onLocalMappingChange={(templateId, mapping) => {
          setLocalUploadRefTemplateMappingById((prev) => ({ ...prev, [templateId]: mapping }));
        }}
      />

      <Dialog
        open={Boolean(mappingConfirmAction)}
        onClose={() => !(refreshingMapping || pushingMapping) && setMappingConfirmAction(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          {mappingConfirmAction === 'push'
            ? 'Push Mapping to Snowflake?'
            : 'Refresh Mapping from Snowflake?'}
        </DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <DialogContentText>
            {mappingConfirmAction === 'push'
              ? 'This will push GL account mappings from Postgres into Snowflake. Fund PROPERTY_ID values already in Snowflake are preserved. Matching rows will be updated.'
              : 'This will load GL account mappings from Snowflake into Postgres. Postgres property_id will be set to null (fund keys stay in Snowflake only). Matching rows will be updated.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setMappingConfirmAction(null)}
            disabled={refreshingMapping || pushingMapping}
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              mappingConfirmAction === 'push' ? handlePushMapping() : handleRefreshMapping()
            }
            disabled={refreshingMapping || pushingMapping}
            startIcon={
              refreshingMapping || pushingMapping ? (
                <CircularProgress size={16} color="inherit" />
              ) : mappingConfirmAction === 'push' ? (
                <CloudUploadIcon />
              ) : (
                <CloudDownloadIcon />
              )
            }
            sx={harmonateProceedButtonSx}
          >
            {refreshingMapping || pushingMapping
              ? mappingConfirmAction === 'push'
                ? 'Pushing…'
                : 'Refreshing…'
              : mappingConfirmAction === 'push'
                ? 'Push Mapping'
                : 'Refresh Mapping'}
          </Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
      />
    </Box>
  );
};

export default GLSyncMapping;